<template>
  <v-container fluid class="pa-4">
    <!-- Section Rapports Financiers Restaurant -->
    <v-card elevation="2">
      <v-card-title class="white--text d-flex justify-space-between align-center" style="background-color: #607D8B;">
        <div>
          <v-icon left dark>mdi-store</v-icon>
          Rapports financiers - {{ infosEnterpriseReference.nom }}
        </div>
        <div class="d-flex">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  dark
                  @click="exportToExcel"
                  :loading="exportLoading"
                  class="mr-2"
                  :disabled="filteredTransactions.length === 0"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-file-excel-outline</v-icon>
              </v-btn>
            </template>
            <span>Exporter en Excel</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  dark
                  @click="refreshData"
                  :loading="loading"
                  v-bind="attrs"
                  v-on="on"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Actualiser les données</span>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-alert color="info" text dense class="mb-4">
          <v-icon left small>mdi-information</v-icon>
          Retrouvez ici les rapports financiers complets du restaurant avec les détails des transactions et commandes.
        </v-alert>

        <!-- Section Informations Globales -->
        <v-card class="mb-4" outlined>
          <v-card-title class="primary white--text">
            <v-icon left>mdi-chart-line</v-icon>
            Informations Globales - {{ infosEnterpriseReference.nom }}
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <!-- Revenus Mois Actuel -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #2196F3;">
                  <v-icon size="48" color="blue">mdi-trending-up</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.globalRevenueActualMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">CA Global Mois Actuel</p>
                  <p class="text-caption grey--text">{{ getCurrentMonthName() }}</p>
                </v-card>
              </v-col>

              <!-- Quote-part Entreprise Mois Actuel -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #FF9800;">
                  <v-icon size="48" color="orange">mdi-domain</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.amountDueByCompanyActualMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">Montant à payer par - {{ infosEnterpriseReference.nom }}</p>
                  <p class="text-caption grey--text">Mois actuel</p>
                </v-card>
              </v-col>

              <!-- Montant Prepaye Employes -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #4CAF50;">
                  <v-icon size="48" color="green">mdi-account-cash</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.amountAlreadyPaidByEmployeeActualMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">Achats Prepayes</p>
                  <p class="text-caption grey--text">Employes - Mois actuel</p>
                </v-card>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <v-row>
              <!-- Revenus Mois Precedent -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #9C27B0;">
                  <v-icon size="48" color="purple">mdi-history</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.globalRevenuePreviousMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">CA Global Mois Precedent</p>
                  <p class="text-caption grey--text">{{ getPreviousMonthName() }}</p>
                </v-card>
              </v-col>

              <!-- Quote-part Entreprise Mois Precedent -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #795548;">
                  <v-icon size="48" color="brown">mdi-office-building</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.amountDueByCompanyPreviousMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">Montant à payer par - {{ infosEnterpriseReference.nom }}</p>
                  <p class="text-caption grey--text">Mois precedent</p>
                </v-card>
              </v-col>

              <!-- Montant Prepaye Employes Mois Precedent -->
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-3" style="border-top: 4px solid #607D8B;">
                  <v-icon size="48" color="blue-grey">mdi-account-group</v-icon>
                  <h2 class="mt-3">{{ balanceInfos.amountAlreadyPaidByEmployeePreviousMonth }}</h2>
                  <p class="text-subtitle-2 grey--text">Achats Prepayes</p>
                  <p class="text-caption grey--text">Employes - Mois precedent</p>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section Détails Financiers -->
        <v-card class="mb-4" outlined>
          <v-card-title class="success white--text">
            <v-icon left>mdi-calculator</v-icon>
            Détails Financiers
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <!-- Transactions Mois Actuel -->
              <v-col cols="12" md="5">
                <!-- Titre du mois avec mise en évidence améliorée -->
                <v-card elevation="0" class="mb-4" color="primary lighten-5">
                  <v-card-text class="d-flex align-center justify-center pa-3">
                    <v-icon left color="primary">mdi-calendar-today</v-icon>
                    <h3 class="primary--text text-h5 font-weight-bold mb-0">{{ getCurrentMonthName() }}</h3>
                  </v-card-text>
                </v-card>

                <v-row>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="primary">mdi-receipt</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.nctwWalletTransactionsActualMonthSite }}</div>
                        <div class="text-caption">Nbr Transactions</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="success">mdi-cash</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.sumPriceTransactionsActualMonthSite }}</div>
                        <div class="text-caption">Total Transactions</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row class="mt-2">
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="orange">mdi-domain</v-icon>
                        <div class="font-weight-bold">{{
                            financialDetails.sumPriceTransactionsActualMonthCompanySite
                          }}
                        </div>
                        <div class="text-caption">Part Entreprise</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="purple">mdi-account</v-icon>
                        <div class="font-weight-bold">{{
                            financialDetails.sumPriceTransactionsActualMonthPersoSite
                          }}
                        </div>
                        <div class="text-caption">Part Personnel</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>

              <!-- Colonne séparatrice avec ligne verticale -->
              <v-col cols="12" md="2" class="d-none d-md-flex align-center justify-center">
                <div style="width: 2px; height: 80%; background-color: #E0E0E0;"></div>
              </v-col>

              <!-- Divider horizontal sur mobile uniquement -->
              <v-col cols="12" class="d-md-none py-2">
                <v-divider></v-divider>
              </v-col>

              <!-- Transactions Mois Precedent -->
              <v-col cols="12" md="5">
                <!-- Titre du mois avec mise en évidence améliorée -->
                <v-card elevation="0" class="mb-4" color="brown lighten-5">
                  <v-card-text class="d-flex align-center justify-center pa-3">
                    <v-icon left color="brown">mdi-calendar-clock</v-icon>
                    <h3 class="brown--text text-h5 font-weight-bold mb-0">{{ getPreviousMonthName() }}</h3>
                  </v-card-text>
                </v-card>

                <v-row>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="primary">mdi-receipt</v-icon>
                        <div class="font-weight-bold">{{
                            financialDetails.nctwWalletTransactionsPreviousMonthSite
                          }}
                        </div>
                        <div class="text-caption">Nbr Transactions</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="success">mdi-cash</v-icon>
                        <div class="font-weight-bold">{{ financialDetails.sumPriceTransactionsPreviousMonthSite }}</div>
                        <div class="text-caption">Total Transactions</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row class="mt-2">
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="orange">mdi-domain</v-icon>
                        <div class="font-weight-bold">{{
                            financialDetails.sumPriceTransactionsPreviousMonthCompanySite
                          }}
                        </div>
                        <div class="text-caption">Part Entreprise</div>
                      </div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card outlined class="pa-2">
                      <div class="text-center">
                        <v-icon color="purple">mdi-account</v-icon>
                        <div class="font-weight-bold">{{
                            financialDetails.sumPriceTransactionsPreviousMonthPersoSite
                          }}
                        </div>
                        <div class="text-caption">Part Personnel</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section Transactions Personnelles (MISE À JOUR COMPLÈTE) -->
        <v-card class="mb-4" outlined>
          <v-card-title class="warning white--text">
            <v-icon left>mdi-account-cash</v-icon>
            Achats privatifs (Compte restaurant)
            <v-spacer></v-spacer>
            <!-- Indicateur de chargement -->
            <v-progress-circular
                v-if="personalTransactionsStats.loading"
                indeterminate
                size="20"
                width="2"
                color="white"
            ></v-progress-circular>
          </v-card-title>

          <v-card-text class="pa-4">
            <!-- Affichage en cas d'erreur -->
            <v-alert v-if="personalTransactionsStats.error" type="error" text dense class="mb-4">
              <v-icon left small>mdi-alert-circle</v-icon>
              {{ personalTransactionsStats.error }}
            </v-alert>

            <!-- NOUVEAU : Message informatif enrichi -->
            <v-alert color="info" text dense class="mb-4">
              <v-icon left small>mdi-information</v-icon>
              <strong>Transactions personnelles :</strong> Achats effectués par les employés avec leur porte-monnaie
              personnel
              (wallet) ou en cash externe (comptoir) lorsqu'ils n'ont plus de quota entreprise.
              <br><small class="grey--text">
              Ces montants sont à facturer au restaurant car ils représentent des achats personnels des salariés.
            </small>
            </v-alert>

            <!-- NOUVEAU : Section totaux avec répartition - DIMENSIONS FIXES -->
            <v-row class="mb-4">
              <!-- Mois Actuel - Total -->
              <v-col cols="12" md="6">
                <v-card
                    outlined
                    class="text-center pa-3 d-flex flex-column"
                    style="border-top: 4px solid #FF5722; min-height: 320px;"
                >
                  <div class="flex-grow-1 d-flex flex-column justify-space-between">
                    <!-- En-tête avec icône -->
                    <div>
                      <v-icon size="48" color="deep-orange">mdi-account-cash</v-icon>

                      <!-- Montant total -->
                      <h2 class="mt-3">{{ sensNumerikInfos.actualMonthEncours }}</h2>
                      <p class="text-subtitle-2 grey--text">Total Transactions Personnelles</p>
                      <p class="text-caption grey--text">{{ getCurrentMonthName() }}</p>
                    </div>

                    <!-- NOUVEAU : Détail des transactions avec répartition - TOUJOURS AFFICHÉ -->
                    <div class="mt-3 flex-grow-1">
                      <v-chip
                          small
                          color="deep-orange"
                          dark
                          class="mr-2 mb-1"
                      >
                        {{ formatNumberWithCommas(personalTransactionsStats.currentMonthTotal) }} transactions au total
                      </v-chip>

                      <!-- Répartition Wallet vs External - TOUJOURS AFFICHÉ -->
                      <v-row class="mt-2 no-gutters">
                        <v-col cols="6">
                          <v-chip
                              x-small
                              color="purple"
                              dark
                              class="mr-1"
                          >
                            <v-icon left x-small>mdi-wallet</v-icon>
                            {{ personalTransactionsStats.currentMonthWallet }} wallet
                          </v-chip>
                          <br>
                          <small class="grey--text">{{ getBreakdownInfo('wallet', 'current') }}</small>
                        </v-col>
                        <v-col cols="6">
                          <v-chip
                              x-small
                              color="orange"
                              dark
                              class="ml-1"
                          >
                            <v-icon left x-small>mdi-cash</v-icon>
                            {{ personalTransactionsStats.currentMonthExternal }} cash
                          </v-chip>
                          <br>
                          <small class="grey--text">{{ getBreakdownInfo('external', 'current') }}</small>
                        </v-col>
                      </v-row>

                      <!-- NOUVEAU : Graphique de répartition simple - TOUJOURS AFFICHÉ -->
                      <div class="mt-3">
                        <v-progress-linear
                            :value="personalTransactionsStats.currentMonthTotal > 0 ? personalTransactionsStats.walletPercentage : 50"
                            color="purple"
                            background-color="orange"
                            height="8"
                            rounded
                        ></v-progress-linear>
                        <div class="text-caption grey--text mt-1">
                          <template v-if="personalTransactionsStats.currentMonthTotal > 0">
                            {{ personalTransactionsStats.walletPercentage.toFixed(1) }}% wallet •
                            {{ personalTransactionsStats.externalPercentage.toFixed(1) }}% cash externe
                          </template>
                          <template v-else>
                            <span class="grey--text">Aucune transaction ce mois</span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>

              <!-- Mois Précédent - Total -->
              <v-col cols="12" md="6">
                <v-card
                    outlined
                    class="text-center pa-3 d-flex flex-column"
                    style="border-top: 4px solid #E91E63; min-height: 320px;"
                >
                  <div class="flex-grow-1 d-flex flex-column justify-space-between">
                    <!-- En-tête avec icône -->
                    <div>
                      <v-icon size="48" color="pink">mdi-history</v-icon>

                      <!-- Montant total précédent -->
                      <h2 class="mt-3">{{ sensNumerikInfos.previousMonthEncours }}</h2>
                      <p class="text-subtitle-2 grey--text">Total Transactions Personnelles</p>
                      <p class="text-caption grey--text">{{ getPreviousMonthName() }}</p>
                    </div>

                    <!-- Détail des transactions précédentes - TOUJOURS AFFICHÉ -->
                    <div class="mt-3 flex-grow-1">
                      <v-chip
                          small
                          color="pink"
                          dark
                          class="mr-2 mb-1"
                      >
                        {{ formatNumberWithCommas(personalTransactionsStats.previousMonthTotal) }} transactions au total
                      </v-chip>

                      <!-- Répartition précédente - TOUJOURS AFFICHÉ -->
                      <v-row class="mt-2 no-gutters">
                        <v-col cols="6">
                          <v-chip
                              x-small
                              color="purple"
                              outlined
                              class="mr-1"
                          >
                            <v-icon left x-small>mdi-wallet</v-icon>
                            {{ personalTransactionsStats.previousMonthWallet }}
                          </v-chip>
                          <br>
                          <small class="grey--text">{{ getBreakdownInfo('wallet', 'previous') }}</small>
                        </v-col>
                        <v-col cols="6">
                          <v-chip
                              x-small
                              color="orange"
                              outlined
                              class="ml-1"
                          >
                            <v-icon left x-small>mdi-cash</v-icon>
                            {{ personalTransactionsStats.previousMonthExternal }}
                          </v-chip>
                          <br>
                          <small class="grey--text">{{ getBreakdownInfo('external', 'previous') }}</small>
                        </v-col>
                      </v-row>

                      <!-- Graphique de répartition précédent - TOUJOURS AFFICHÉ -->
                      <div class="mt-3">
                        <v-progress-linear
                            :value="personalTransactionsStats.previousMonthTotal > 0 ?
                      (personalTransactionsStats.previousMonthWallet / personalTransactionsStats.previousMonthTotal * 100) : 50"
                            color="purple"
                            background-color="orange"
                            height="8"
                            rounded
                        ></v-progress-linear>
                        <div class="text-caption grey--text mt-1">
                          <template v-if="personalTransactionsStats.previousMonthTotal > 0">
                            {{
                              ((personalTransactionsStats.previousMonthWallet / personalTransactionsStats.previousMonthTotal) * 100).toFixed(1)
                            }}% wallet •
                            {{
                              ((personalTransactionsStats.previousMonthExternal / personalTransactionsStats.previousMonthTotal) * 100).toFixed(1)
                            }}% cash externe
                          </template>
                          <template v-else>
                            <span class="grey--text">Aucune transaction ce mois</span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section Historiques avec Onglets -->
        <v-card class="mb-4" outlined>
          <v-card-title class="info white--text">
            <v-icon left>mdi-history</v-icon>
            Historiques - {{ infosEnterpriseReference.nom }}
          </v-card-title>

          <v-card-text class="pa-0">
            <!-- ✅ NOUVELLE VERSION RESPONSIVE -->
            <v-tabs
                v-model="activeTab"
                :centered="$vuetify.breakpoint.mdAndUp"
                :icons-and-text="$vuetify.breakpoint.mdAndUp"
                :height="$vuetify.breakpoint.mobile ? 48 : 60"
                :show-arrows="$vuetify.breakpoint.mobile"
                :slider-size="$vuetify.breakpoint.mobile ? 2 : 4"
                class="tabs-responsive"
            >
              <v-tab href="#tab-transactions" class="tab-item">
                <v-icon :class="$vuetify.breakpoint.mobile ? '' : 'mb-1'" :size="$vuetify.breakpoint.mobile ? 20 : 24">
                  mdi-silverware-fork-knife
                </v-icon>
                <span :class="$vuetify.breakpoint.mobile ? 'ml-1' : ''">
                  {{ $vuetify.breakpoint.mobile ? 'Transactions' : 'Historique des Transactions' }}
                </span>
              </v-tab>

              <v-tab href="#tab-paiements-externes" class="tab-item">
                <v-icon :class="$vuetify.breakpoint.mobile ? '' : 'mb-1'" :size="$vuetify.breakpoint.mobile ? 20 : 24">
                  mdi-wallet-outline
                </v-icon>
                <span :class="$vuetify.breakpoint.mobile ? 'ml-1' : ''">
                {{ $vuetify.breakpoint.mobile ? 'Cash+' : 'Paiements Cash+' }}
              </span>
              </v-tab>

              <v-tab href="#tab-recharges" class="tab-item">
                <v-icon :class="$vuetify.breakpoint.mobile ? '' : 'mb-1'" :size="$vuetify.breakpoint.mobile ? 20 : 24">
                  mdi-wallet-plus
                </v-icon>
                <span :class="$vuetify.breakpoint.mobile ? 'ml-1' : ''">
                  {{ $vuetify.breakpoint.mobile ? 'Recharges' : 'Historique des Recharges' }}
                </span>
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- Tab Historique des Transactions -->
              <v-tab-item value="tab-transactions">
                <v-container fluid class="pa-4">
                  <!-- Filtres et controles -->
                  <v-card class="mb-4" outlined>
                    <v-card-text class="pa-4">
                      <v-row align="center">
                        <v-col cols="12" sm="6" md="4">
                          <v-select
                              v-model="depthVal"
                              :items="selectDates"
                              item-text="name"
                              item-value="value"
                              label="Profondeur"
                              outlined
                              dense
                              return-object
                              @change="depthDate"
                          ></v-select>
                        </v-col>

                        <v-col cols="12" sm="6" md="4">
                          <v-select
                              v-model="heightVal"
                              :items="selectDatesHeight"
                              item-text="name"
                              item-value="value"
                              label="Hauteur"
                              outlined
                              dense
                              return-object
                              @change="heightDate"
                          ></v-select>
                        </v-col>

                        <v-col cols="12" md="4">
                          <v-text-field
                              v-model="searchName"
                              label="Rechercher..."
                              prepend-icon="mdi-magnify"
                              outlined
                              dense
                              clearable
                              @click:clear="cleared"
                              @input="search"
                              placeholder="Nom, prenom, matricule, montant, statut..."
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>

                  <!-- Tableau des transactions -->
                  <v-data-table
                      :headers="headers"
                      :items="filteredTransactions"
                      :items-per-page="15"
                      class="elevation-1"
                      :loading="loading"
                      loading-text="Chargement des données..."
                      disable-sort
                  >
                    <template v-slot:[`item.entreprise`]="{ item }">
                      {{ item.entreprise }}
                    </template>

                    <template v-slot:[`item.employe`]="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar size="32" class="mr-3" :color="getEmployeeColor(item.matricule)">
                  <span class="white--text text-caption font-weight-bold">
                    {{ getInitials(item.nom, item.prenom) }}
                  </span>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold">{{ item.nom }} {{ item.prenom }}</div>
                          <div class="text-caption grey--text">{{ item.matricule }}</div>
                        </div>
                      </div>
                    </template>

                    <template v-slot:[`item.dateCommande`]="{ item }">
                      <v-chip
                          small
                          :color="getDateColor(item.dateCommande)"
                          dark
                      >
                        {{ convertDate(item.dateCommande) }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.montant`]="{ item }">
                      <div class="text-right font-weight-bold">
                        {{ item.montant }} FCFA
                      </div>
                    </template>

                    <template v-slot:[`item.statut`]="{ item }">
                      <v-chip
                          small
                          :color="getStatusColor(item.statut)"
                          dark
                      >
                        {{ item.statut }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.niveau`]="{ item }">
                      <v-chip
                          small
                          :color="getNiveauColor(item.niveau)"
                          :outlined="item.niveau !== 'PRINCIPAL'"
                          :dark="item.niveau === 'PRINCIPAL'"
                      >
                        {{ formatNiveau(item.niveau) }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn
                          icon
                          small
                          @click="viewTransactionDetails(item)"
                          color="primary"
                      >
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                      <v-btn
                          v-if="canDelete(item)"
                          icon
                          small
                          color="error"
                          @click="deleteTransaction(item)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>

              <!-- Tab Paiements Externes (NOUVEAU) -->
              <v-tab-item value="tab-paiements-externes">
                <v-container fluid class="pa-4">
                  <v-alert color="warning" text dense class="mb-4">
                    <v-icon left small>mdi-information</v-icon>
                    Historique des paiements externes (Cash, Wave, Orange Money, etc.) effectués par les employés de
                    <strong>{{ infosEnterpriseReference.nom }}</strong> dans ce restaurant.
                  </v-alert>

                  <!-- Statistiques des paiements externes -->
                  <v-row class="mb-4" v-if="externalStats">
                    <v-col cols="12" sm="6" md="3">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #FF9800;">
                        <v-icon size="40" color="orange">mdi-cash-multiple</v-icon>
                        <h3 class="mt-2">{{ getExternalStatValue('totalAmount') }}</h3>
                        <p class="text-subtitle-2 grey--text">💰 Total Dépensé</p>
                        <p class="text-caption grey--text">{{ getExternalStatValue('totalCount') }} transactions</p>
                      </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #4CAF50;">
                        <v-icon size="40" color="green">mdi-check-circle</v-icon>
                        <h3 class="mt-2">{{ getExternalStatValue('successCount') }}</h3>
                        <p class="text-subtitle-2 grey--text">✅ Réussies</p>
                        <p class="text-caption grey--text">{{ getExternalStatValue('successAmount') }} FCFA</p>
                      </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #2196F3;">
                        <v-icon size="40" color="blue">mdi-clock</v-icon>
                        <h3 class="mt-2">{{ getExternalStatValue('pendingCount') }}</h3>
                        <p class="text-subtitle-2 grey--text">⏳ En Attente</p>
                        <p class="text-caption grey--text">{{ getExternalStatValue('pendingAmount') }} FCFA</p>
                      </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #9C27B0;">
                        <v-icon size="40" color="purple">mdi-calendar</v-icon>
                        <h3 class="mt-2">{{ getLastExternalPaymentDate() }}</h3>
                        <p class="text-subtitle-2 grey--text">📅 Dernier Paiement</p>
                        <p class="text-caption grey--text">{{ getLastExternalPaymentAmount() }}</p>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Boutons de sélection rapide pour paiements externes -->
                  <v-row class="mb-3">
                    <v-col cols="12" class="text-center">
                      <v-btn-toggle v-model="selectedPeriodExternal" mandatory color="warning" dense
                                    class="flex-wrap justify-center">
                        <v-btn value="today" small>
                          <v-icon left small>mdi-calendar-today</v-icon>
                          Aujourd'hui
                        </v-btn>
                        <v-btn value="week" small>
                          <v-icon left small>mdi-calendar-week</v-icon>
                          Cette semaine
                        </v-btn>
                        <v-btn value="month" small>
                          <v-icon left small>mdi-calendar-month</v-icon>
                          Ce mois
                        </v-btn>
                        <v-btn value="last_month" small>
                          <v-icon left small>mdi-calendar-minus</v-icon>
                          Mois dernier
                        </v-btn>
                        <v-btn value="custom" small>
                          <v-icon left small>mdi-calendar-range</v-icon>
                          Personnalisé
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                  </v-row>

                  <!-- Sélecteur de plage personnalisée -->
                  <v-expand-transition>
                    <v-row v-show="selectedPeriodExternal === 'custom'" class="mb-3">
                      <v-col cols="12" sm="6">
                        <v-menu v-model="menuDateDebutExternal" :close-on-content-click="false"
                                transition="scale-transition" offset-y min-width="290px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field :value="formatDateFR(dateDebutExternal)" label="Date de début" prepend-icon="mdi-calendar"
                                          readonly outlined dense v-bind="attrs" v-on="on"></v-text-field>
                          </template>
                          <v-date-picker v-model="dateDebutExternal" @input="menuDateDebutExternal = false"
                                         locale="fr-FR"
                                         :first-day-of-week="1"></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-menu v-model="menuDateFinExternal" :close-on-content-click="false"
                                transition="scale-transition" offset-y min-width="290px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field :value="formatDateFR(dateFinExternal)" label="Date de fin" prepend-icon="mdi-calendar"
                                          readonly outlined dense v-bind="attrs" v-on="on"></v-text-field>
                          </template>
                          <v-date-picker v-model="dateFinExternal" @input="menuDateFinExternal = false"
                                         locale="fr-FR"
                                         :first-day-of-week="1"></v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                  </v-expand-transition>

                  <!-- Filtres pour paiements externes -->
                  <v-row class="mb-4">
                    <v-col cols="12" sm="4">
                      <v-select v-model="selectedPaymentMethod" :items="paymentMethodOptions"
                                label="Méthode de paiement" prepend-icon="mdi-credit-card" outlined dense></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-select v-model="selectedStatusExternal" :items="statusExternalOptions" label="Statut"
                                prepend-icon="mdi-check-circle" outlined dense></v-select>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-text-field v-model="searchQueryExternal" label="Rechercher..." prepend-icon="mdi-magnify"
                                    outlined dense clearable @click:clear="clearSearchExternal"></v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Tableau des paiements externes -->
                  <v-data-table :headers="headersExternal" :items="filteredExternalPayments" :items-per-page="10"
                                class="elevation-1" :loading="loadingExternal"
                                loading-text="Chargement des paiements externes..."
                                :no-data-text="getNoExternalDataText()">
                    <template v-slot:[`item.employee`]="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar size="32" class="mr-3" :color="getEmployeeColor(item.employeeId)">
                          <span class="white--text text-caption font-weight-bold">{{
                              getInitials(item.employeeLastname, item.employeeFirstname)
                            }}</span>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold">{{ item.employeeLastname }} {{ item.employeeFirstname }}</div>
                          <div class="text-caption grey--text">{{ item.employeeId }}</div>
                        </div>
                      </div>
                    </template>
                    <template v-slot:[`item.transactionDate`]="{ item }">{{
                        formatExternalDate(item.transactionDate)
                      }}
                    </template>
                    <template v-slot:[`item.amount`]="{ item }">
                      <div class="d-flex align-center justify-end">
                        <span class="font-weight-bold text-h6 orange--text">{{
                            formatExternalAmount(item.amount)
                          }}</span>
                      </div>
                    </template>
                    <template v-slot:[`item.paymentMethod`]="{ item }">
                      <v-chip small :color="getPaymentMethodColor(item.paymentMethod, item)" dark>
                        <v-icon left small>{{ getPaymentMethodIcon(item.paymentMethod, item) }}</v-icon>
                        {{ item.paymentMethod }}
                      </v-chip>
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                      <v-chip small :color="getExternalStatusColor(item.status)">
                        <v-icon left small>{{ getExternalStatusIcon(item.status) }}</v-icon>
                        {{ item.status }}
                      </v-chip>
                    </template>
                    <template v-slot:[`item.typeAuth`]="{ item }">
                      <v-chip x-small :color="getAuthColor(item.typeAuth)" outlined>{{ item.typeAuth }}</v-chip>
                    </template>
                    <template v-slot:[`item.customerType`]="{ item }">
                      <v-chip x-small :color="item.customerType === 'Identifié' ? 'success' : 'info'" outlined>
                        {{ item.customerType }}
                      </v-chip>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn icon small @click="viewDetailsExternal(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <template v-slot:no-data>
                      <div class="text-center pa-6">
                        <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-wallet-outline</v-icon>
                        <h3 class="grey--text text--lighten-1 mb-2">{{ getNoExternalDataTitle() }}</h3>
                        <p class="grey--text">{{ getNoExternalDataMessage() }}</p>
                        <v-btn v-if="!loadingExternal && externalPayments.length === 0" color="warning" outlined
                               @click="loadExternalPayments" class="mt-2">
                          <v-icon left>mdi-refresh</v-icon>
                          Actualiser
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>

              <!-- Tab Historique des Recharges -->
              <v-tab-item value="tab-recharges">
                <v-container fluid class="pa-4">
                  <v-alert color="success" text dense class="mb-4">
                    <v-icon left small>mdi-information</v-icon>
                    Consultez ici toutes les recharges effectuées par les employés de
                    <strong>{{ infosEnterpriseReference.nom }}</strong>.
                  </v-alert>

                  <!-- Statistiques des recharges -->
                  <v-row class="mb-4" v-if="rechargeStats">
                    <v-col cols="12" sm="6">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #4CAF50;">
                        <v-icon size="40" color="green">mdi-wallet-plus</v-icon>
                        <h3 class="mt-2">{{ getStatValue('totalCredits') }}</h3>
                        <p class="text-subtitle-2 grey--text">💰 Total Crédits</p>
                        <p class="text-caption grey--text">{{ getStatValue('countCredits') }} recharges</p>
                      </v-card>
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-card outlined class="text-center pa-3" style="border-top: 4px solid #FF9800;">
                        <v-icon size="40" color="orange">mdi-history</v-icon>
                        <h3 class="mt-2">{{ getLastRechargeDate() }}</h3>
                        <p class="text-subtitle-2 grey--text">📅 Dernière Recharge</p>
                        <p class="text-caption grey--text">{{ getLastRechargeAmount() }}</p>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Boutons de sélection rapide pour recharges -->
                  <v-row class="mb-3">
                    <v-col cols="12" class="text-center">
                      <v-btn-toggle
                          v-model="selectedPeriodRecharges"
                          mandatory
                          color="success"
                          dense
                          class="flex-wrap justify-center"
                      >
                        <v-btn value="today" small>
                          <v-icon left small>mdi-calendar-today</v-icon>
                          Aujourd'hui
                        </v-btn>
                        <v-btn value="week" small>
                          <v-icon left small>mdi-calendar-week</v-icon>
                          Cette semaine
                        </v-btn>
                        <v-btn value="month" small>
                          <v-icon left small>mdi-calendar-month</v-icon>
                          Ce mois
                        </v-btn>
                        <v-btn value="last_month" small>
                          <v-icon left small>mdi-calendar-minus</v-icon>
                          Mois dernier
                        </v-btn>
                        <v-btn value="custom" small>
                          <v-icon left small>mdi-calendar-range</v-icon>
                          Personnalisé
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                  </v-row>

                  <!-- Sélecteur de plage personnalisée pour recharges -->
                  <v-expand-transition>
                    <v-row v-show="selectedPeriodRecharges === 'custom'" class="mb-3">
                      <v-col cols="12" sm="6">
                        <v-menu
                            v-model="menuDateDebutRecharges"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                :value="formatDateFR(dateDebutRecharges)"
                                label="Date de début"
                                prepend-icon="mdi-calendar"
                                readonly
                                outlined
                                dense
                                v-bind="attrs"
                                v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                              v-model="dateDebutRecharges"
                              @input="menuDateDebutRecharges = false"
                              locale="fr-FR"
                              :first-day-of-week="1"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-menu
                            v-model="menuDateFinRecharges"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                :value="formatDateFR(dateFinRecharges)"
                                label="Date de fin"
                                prepend-icon="mdi-calendar"
                                readonly
                                outlined
                                dense
                                v-bind="attrs"
                                v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                              v-model="dateFinRecharges"
                              @input="menuDateFinRecharges = false"
                              locale="fr-FR"
                              :first-day-of-week="1"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                  </v-expand-transition>

                  <!-- Filtres pour recharges -->
                  <v-row class="mb-4">
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                          v-model="selectedTypeRecharge"
                          :items="typeRechargeOptions"
                          label="Type d'opération"
                          prepend-icon="mdi-swap-vertical"
                          outlined
                          dense
                      ></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="3">
                      <v-select
                          v-model="selectedStatusRecharge"
                          :items="statusRechargeOptions"
                          label="Statut"
                          prepend-icon="mdi-check-circle"
                          outlined
                          dense
                      ></v-select>
                    </v-col>

                    <!-- NEW: Payment Method Filter -->
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                          v-model="selectedPaymentMethodRecharge"
                          :items="paymentMethodRechargeOptions"
                          label="Méthode de paiement"
                          prepend-icon="mdi-credit-card"
                          outlined
                          dense
                      ></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                          v-model="searchQueryRecharges"
                          label="Rechercher..."
                          prepend-icon="mdi-magnify"
                          outlined
                          dense
                          clearable
                          @click:clear="clearSearchRecharges"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Tableau des recharges -->
                  <v-data-table
                      :headers="headersRecharges"
                      :items="filteredRecharges"
                      :items-per-page="10"
                      class="elevation-1"
                      :loading="loadingRecharges"
                      loading-text="Chargement des recharges..."
                      :no-data-text="getNoDataText()"
                  >
                    <template v-slot:[`item.employee`]="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar size="32" class="mr-3" :color="getEmployeeColor(item.employeeId)">
                  <span class="white--text text-caption font-weight-bold">
                    {{ getInitials(item.employeeLastname, item.employeeFirstname) }}
                  </span>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold">{{ item.employeeLastname }} {{ item.employeeFirstname }}</div>
                          <div class="text-caption grey--text">{{ item.employeeId }}</div>
                        </div>
                      </div>
                    </template>

                    <template v-slot:[`item.dateTransaction`]="{ item }">
                      {{ formatDate(item.dateTransaction) }}
                    </template>

                    <template v-slot:[`item.amount`]="{ item }">
                      <div class="d-flex align-center justify-end">
                <span :class="getAmountClass(item.typeOperation)">
                  {{ item.typeOperation === 'DEBIT' ? '-' : '+' }}{{ item.amount }} FCFA
                </span>
                      </div>
                    </template>

                    <template v-slot:[`item.typeOperation`]="{ item }">
                      <v-chip small :color="getTypeOperationColor(item.typeOperation)" dark>
                        <v-icon left small>{{ getTypeOperationIcon(item.typeOperation) }}</v-icon>
                        {{ item.typeOperation }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.status`]="{ item }">
                      <v-chip small :color="getStatusColorRecharge(item.status)">
                        <v-icon left small>{{ getStatusIconRecharge(item.status) }}</v-icon>
                        {{ item.status }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.level`]="{ item }">
                      <v-chip small :color="getNiveauColor(item.level)" outlined>
                        {{ formatNiveau(item.level) }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.typeAuth`]="{ item }">
                      <v-chip x-small :color="getAuthColor(item.typeAuth)" outlined>
                        {{ item.typeAuth }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn icon small @click="viewDetailsRecharge(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                      <v-btn
                          v-if="canCancelRecharge(item)"
                          icon
                          small
                          color="warning"
                          @click="cancelRecharge(item)"
                      >
                        <v-icon small>mdi-cancel</v-icon>
                      </v-btn>
                    </template>

                    <!-- Enhanced no-data slot -->
                    <template v-slot:no-data>
                      <div class="text-center pa-6">
                        <v-icon size="64" color="grey lighten-1" class="mb-4">
                          mdi-wallet-outline
                        </v-icon>
                        <h3 class="grey--text text--lighten-1 mb-2">
                          {{ getNoDataTitle() }}
                        </h3>
                        <p class="grey--text">
                          {{ getNoDataMessage() }}
                        </p>
                        <v-btn
                            v-if="!loadingRecharges && recharges.length === 0"
                            color="primary"
                            outlined
                            @click="loadRecharges"
                            class="mt-2"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Actualiser
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>

        <!-- Dialog pour les détails de recharge -->
        <v-dialog v-model="detailsRechargeDialog" max-width="500">
          <v-card>
            <v-card-title class="headline success white--text">
              <v-icon left dark>mdi-wallet-plus</v-icon>
              Détails de la recharge
            </v-card-title>
            <v-card-text class="pt-4">
              <div v-html="detailsRechargeContent"></div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="success" text @click="detailsRechargeDialog = false">
                Fermer
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Dialog pour les détails de paiement externe (NOUVEAU) -->
        <v-dialog v-model="detailsExternalDialog" max-width="500">
          <v-card>
            <v-card-title class="headline warning white--text">
              <v-icon left dark>mdi-wallet-outline</v-icon>
              Détails du paiement externe
            </v-card-title>
            <v-card-text class="pt-4">
              <div v-html="detailsExternalContent"></div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="warning" text @click="detailsExternalDialog = false">Fermer</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>

    <!-- Dialog pour les détails de transaction -->
    <v-dialog v-model="detailsDialog" max-width="350">
      <v-card>
        <v-card-title class="headline white--text" style="background-color: #03A9F4;">
          <v-icon left dark>mdi-receipt</v-icon>
          Détails de la transaction
        </v-card-title>
        <v-card-text class="pt-4" v-if="selectedTransaction">
          <v-row>
            <v-col cols="12">
              <div v-html="transactionDetailsHtml"></div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailsDialog = false">
            Fermer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog pour les alertes -->
    <v-dialog v-model="alertDialog" max-width="500">
      <v-card>
        <v-card-title :class="alertColor === 'success' ? 'success' : 'error'" class="white--text">
          {{ alertTitle }}
        </v-card-title>
        <v-card-text class="pt-4">
          {{ alertMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="alertDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading overlay -->
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import apiClient from "../../../config/api.js";
import * as XLSX from "xlsx";
import moment from "moment";
import {depthDate} from "../../../helper/profondeur";
import {heightDate} from "../../../helper/hauteur";

export default {
  name: "RapportsRestaurant",
  data() {
    return {
      // NOUVEAU: Paiements externes
      externalPayments: [],
      externalStats: null,
      loadingExternal: false,

      // NOUVEAU: Filtres pour paiements externes
      selectedPaymentMethod: "Tous",
      selectedStatusExternal: "Tous",
      searchQueryExternal: "",
      selectedPeriodExternal: "month",
      dateDebutExternal: moment().startOf('month').format('YYYY-MM-DD'),
      dateFinExternal: moment().endOf('month').format('YYYY-MM-DD'),

      // NOUVEAU: Menus dates externes
      menuDateDebutExternal: false,
      menuDateFinExternal: false,

      // NOUVEAU: Dialog externe
      detailsExternalDialog: false,
      detailsExternalContent: "",

      // NOUVEAU: Options externes
      paymentMethodOptions: ["Tous", "Cash", "Wave", "Orange Money", "Autre"],
      statusExternalOptions: ["Tous", "SUCCESS", "PENDING", "FAILED", "CANCELLED"],

      // NOUVEAU: Headers tableau externe
      headersExternal: [
        {text: "Employé", value: "employee", align: "start"},
        {text: "Date", value: "transactionDate", align: "start"},
        {text: "Montant", value: "amount", align: "end"},
        {text: "Méthode", value: "paymentMethod", align: "center"},
        {text: "Type Client", value: "customerType", align: "center"},
        {text: "Authentification", value: "typeAuth", align: "center"},
        {text: "Statut", value: "status", align: "center"},
        {text: "Description", value: "description"},
        {text: "Actions", value: "actions", align: "center", sortable: false}
      ],

      // Enhanced tabs and recharge data
      activeTab: "tab-transactions",
      recharges: [],
      rechargeStats: null,
      loadingRecharges: false,

      // Enhanced filters for recharges
      selectedTypeRecharge: "Tous",
      selectedStatusRecharge: "Tous",
      searchQueryRecharges: "",
      selectedPeriodRecharges: "month",
      dateDebutRecharges: moment().startOf('month').format('YYYY-MM-DD'),
      dateFinRecharges: moment().endOf('month').format('YYYY-MM-DD'),

      // Enhanced date menus
      menuDateDebutRecharges: false,
      menuDateFinRecharges: false,

      // Enhanced dialogs
      detailsRechargeDialog: false,
      detailsRechargeContent: "",

      // Enhanced options
      typeRechargeOptions: ["Tous", "CREDIT", "DEBIT"],
      statusRechargeOptions: ["Tous", "SUCCESS", "FAILED", "CANCELLED", "PENDING"],

      // Enhanced headers for recharge table
      headersRecharges: [
        {text: "Employé", value: "employee", align: "start"},
        {text: "Date", value: "dateTransaction", align: "start"},
        {text: "Montant", value: "amount", align: "end"},
        {text: "Type", value: "typeOperation", align: "center"},
        {text: "Authentification", value: "typeAuth", align: "center"},
        {text: "Niveau", value: "level", align: "center"},
        {text: "Statut", value: "status", align: "center"},
        {text: "Raison", value: "reason"},
        {text: "Actions", value: "actions", align: "center", sortable: false}
      ],

      // Enhanced personal transactions stats
      personalTransactionsStats: {
        // Nouvelles propriétés pour les stats combinées
        currentMonthWallet: 0,
        currentMonthExternal: 0,
        currentMonthTotal: 0,
        previousMonthWallet: 0,
        previousMonthExternal: 0,
        previousMonthTotal: 0,
        difference: 0,
        percentageChange: 0,

        // Nouvelles propriétés pour l'analyse détaillée
        walletPercentage: 0,
        externalPercentage: 0,
        externalGrowthRate: 0,

        // États
        loading: false,
        error: null,

        // Détails pour l'affichage
        breakdown: {
          wallet: {
            description: "Transactions via porte-monnaie personnel (solde épuisé)",
            currentAmount: 0,
            previousAmount: 0
          },
          external: {
            description: "Transactions externes 100% cash (paiement comptoir)",
            currentAmount: 0,
            previousAmount: 0
          }
        }
      },

      loading: false,
      exportLoading: false,
      alertDialog: false,
      alertTitle: "",
      alertMessage: "",
      alertColor: "info",
      detailsDialog: false,
      selectedTransaction: null,

      // Enhanced properties for unit price and Sens NumeriK info
      unitPriceTransaction: 0,
      sensNumerikInfos: {
        actualMonthEncours: "Chargement...",
        previousMonthEncours: "Chargement..."
      },

      // Restaurant data
      transactions: [],
      searchName: "",

      // Date selects
      selectDates: depthDate,
      selectDatesHeight: heightDate,
      depthVal: {
        name: "Profondeur Hier",
        value: "1"
      },
      heightVal: {
        name: "Hauteur Demain",
        value: "1"
      },

      // Company information
      infosEnterpriseReference: {
        id: "",
        nom: "TSA",
        address: "",
        phone: ""
      },

      // Financial data
      balanceInfos: {
        matricule: "",
        companyName: "",
        globalRevenueActualMonth: 0,
        amountDueByCompanyActualMonth: 0,
        amountAlreadyPaidByEmployeeActualMonth: 0,
        globalRevenuePreviousMonth: 0,
        amountDueByCompanyPreviousMonth: 0,
        amountAlreadyPaidByEmployeePreviousMonth: 0
      },

      financialDetails: {
        nctwWalletTransactionsActualMonthSite: 0,
        sumPriceTransactionsActualMonthSite: 0,
        sumPriceTransactionsActualMonthCompanySite: 0,
        sumPriceTransactionsActualMonthPersoSite: 0,
        nctwWalletTransactionsPreviousMonthSite: 0,
        sumPriceTransactionsPreviousMonthSite: 0,
        sumPriceTransactionsPreviousMonthCompanySite: 0,
        sumPriceTransactionsPreviousMonthPersoSite: 0,
        monthActual: "",
        monthPrevious: ""
      },

      // Enhanced headers for transactions table
      headers: [
        {text: "Entreprise", value: "entreprise", align: "start"},
        {text: "Employe", value: "employe", align: "start"},
        {text: "Date Commande", value: "dateCommande", align: "center"},
        {text: "Montant", value: "montant", align: "end"},
        {text: "Type Operation", value: "typeOperation", align: "center"},
        {text: "Paiement", value: "paiement", align: "center"},
        {text: "Statut", value: "statut", align: "center"},
        {text: "Niveau", value: "niveau", align: "center"},
        {text: "Actions", value: "actions", align: "center", sortable: false}
      ]
    };
  },

  watch: {
    // Enhanced watchers for recharges
    selectedPeriodRecharges: {
      handler(newVal) {
        this.updateDateRangeRecharges(newVal);
        if (this.activeTab === 'tab-recharges') {
          // 🔧 SOLUTION RADICALE : Calcul dynamique seulement
          this.$nextTick(() => {
            this.calculateRechargeStatsFromTransactions();
          });
        }
      },
      immediate: false
    },

    filteredRecharges: {
      handler() {
        if (this.activeTab === 'tab-recharges') {
          // 🆕 CORRIGÉ : Délai plus long pour éviter conflit avec API
          setTimeout(() => {
            this.calculateRechargeStatsFromTransactions();
          }, 500);
        }
      },
      immediate: false
    },

    // NOUVEAU: Watcher pour paiements externes avec recalcul des stats
    selectedPeriodExternal: {
      handler(newVal) {
        this.updateDateRangeExternal(newVal);
        if (this.activeTab === 'tab-paiements-externes') {
          this.$nextTick(() => {
            this.calculateExternalStatsFromTransactions();
          });
        }
      },
      immediate: false
    },

    // NOUVEAU: Watcher pour les paiements externes filtrés avec recalcul des stats
    filteredExternalPayments: {
      handler() {
        if (this.activeTab === 'tab-paiements-externes') {
          this.$nextTick(() => {
            this.calculateExternalStatsFromTransactions();
          });
        }
      },
      immediate: false
    },

    activeTab(newVal) {
      console.log("Active tab changed to:", newVal);
      if (newVal === 'tab-recharges') {
        console.log("Loading recharges data for restaurant...");
        this.loadRecharges();
      } else if (newVal === 'tab-paiements-externes') {
        console.log("Loading external payments data for restaurant...");
        this.loadExternalPayments();
        // NOUVEAU: Calculer les stats immédiatement
        this.$nextTick(() => {
          this.calculateExternalStatsFromTransactions();
        });
      }
    }
  },

  computed: {
    // NOUVEAU: Computed pour filtrer les paiements externes avec meilleur logging
    filteredExternalPayments() {
      let filtered = [...this.externalPayments];

      console.log("🔍 Starting external payments filtering with", filtered.length, "payments");

      if (this.dateDebutExternal && this.dateFinExternal) {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(p => {
          if (!p.transactionDate) return false;

          const transDate = this.parseExternalDate(p.transactionDate);
          if (!transDate || !transDate.isValid()) return false;

          const isInRange = transDate.isSameOrAfter(this.dateDebutExternal, 'day') &&
              transDate.isSameOrBefore(this.dateFinExternal, 'day');

          return isInRange;
        });
        console.log(`📅 External date filter: ${beforeFilter} -> ${filtered.length} payments`);
      }

      if (this.selectedPaymentMethod && this.selectedPaymentMethod !== "Tous") {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(p => p.paymentMethod === this.selectedPaymentMethod);
        console.log(`💳 Payment method filter: ${beforeFilter} -> ${filtered.length} payments`);
      }

      if (this.selectedStatusExternal && this.selectedStatusExternal !== "Tous") {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(p => p.status === this.selectedStatusExternal);
        console.log(`✅ Status filter: ${beforeFilter} -> ${filtered.length} payments`);
      }

      if (this.searchQueryExternal) {
        const beforeFilter = filtered.length;
        const query = this.searchQueryExternal.toLowerCase();
        filtered = filtered.filter(p =>
            (p.description && p.description.toLowerCase().includes(query)) ||
            p.amount.toString().includes(query) ||
            (p.transactionUid && p.transactionUid.toLowerCase().includes(query)) ||
            (p.customerName && p.customerName.toLowerCase().includes(query)) ||
            (p.employeeFirstname && p.employeeFirstname.toLowerCase().includes(query)) ||
            (p.employeeLastname && p.employeeLastname.toLowerCase().includes(query)) ||
            (p.employeeId && p.employeeId.toLowerCase().includes(query))
        );
        console.log(`🔍 Search filter: ${beforeFilter} -> ${filtered.length} payments`);
      }

      console.log("✅ Final filtered external payments:", filtered);
      return filtered;
    },

    // Enhanced computed for filtering recharges with debugging
    filteredRecharges() {
      let filtered = [...this.recharges];

      console.log("🔍 Starting recharge filtering with", filtered.length, "recharges");

      if (this.dateDebutRecharges && this.dateFinRecharges) {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(r => {
          if (!r.dateTransaction) {
            console.log("⚠️ Recharge missing dateTransaction:", r);
            return false;
          }
          const transDate = moment(r.dateTransaction);
          const isValid = transDate.isValid();
          const isInRange = transDate.isSameOrAfter(this.dateDebutRecharges, 'day') &&
              transDate.isSameOrBefore(this.dateFinRecharges, 'day');

          if (!isValid) {
            console.log("❌ Invalid recharge date format:", r.dateTransaction, "for recharge:", r);
          }

          return isValid && isInRange;
        });
        console.log(`📅 Recharge date filter: ${beforeFilter} -> ${filtered.length} recharges`);
      }

      if (this.selectedTypeRecharge && this.selectedTypeRecharge !== "Tous") {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(r => r.typeOperation === this.selectedTypeRecharge);
        console.log(`🔄 Recharge type filter: ${beforeFilter} -> ${filtered.length} recharges`);
      }

      if (this.selectedStatusRecharge && this.selectedStatusRecharge !== "Tous") {
        const beforeFilter = filtered.length;
        filtered = filtered.filter(r => r.status === this.selectedStatusRecharge);
        console.log(`✅ Recharge status filter: ${beforeFilter} -> ${filtered.length} recharges`);
      }

      if (this.searchQueryRecharges) {
        const beforeFilter = filtered.length;
        const query = this.searchQueryRecharges.toLowerCase();
        filtered = filtered.filter(r =>
            (r.reason && r.reason.toLowerCase().includes(query)) ||
            r.amount.toString().includes(query) ||
            (r.transactionUid && r.transactionUid.toLowerCase().includes(query)) ||
            (r.employeeFirstname && r.employeeFirstname.toLowerCase().includes(query)) ||
            (r.employeeLastname && r.employeeLastname.toLowerCase().includes(query)) ||
            (r.employeeId && r.employeeId.toLowerCase().includes(query))
        );
        console.log(`🔍 Recharge search filter: ${beforeFilter} -> ${filtered.length} recharges`);
      }

      console.log("✅ Final filtered recharges:", filtered);
      return filtered;
    },

    filteredTransactions() {
      let filtered = [...this.transactions];

      // Enhanced filtering with debugging
      console.log("🔍 Starting transaction filtering with", filtered.length, "transactions");

      if (this.searchName && this.searchName.trim() !== '') {
        const beforeFilter = filtered.length;
        const query = this.searchName.toLowerCase().trim();
        filtered = filtered.filter(transaction => {
          return (
              // Recherche dans l'entreprise
              (transaction.entreprise && transaction.entreprise.toLowerCase().includes(query)) ||
              // Recherche dans le nom
              (transaction.nom && transaction.nom.toLowerCase().includes(query)) ||
              // Recherche dans le prenom
              (transaction.prenom && transaction.prenom.toLowerCase().includes(query)) ||
              // Recherche dans le nom complet
              (transaction.nom && transaction.prenom &&
                  `${transaction.nom} ${transaction.prenom}`.toLowerCase().includes(query)) ||
              // Recherche dans le matricule
              (transaction.matricule && transaction.matricule.toLowerCase().includes(query)) ||
              // Recherche dans la date de commande
              (transaction.dateCommande && transaction.dateCommande.toLowerCase().includes(query)) ||
              // Recherche dans le montant (conversion en string)
              (transaction.montant && transaction.montant.toString().includes(query)) ||
              // Recherche dans le type d'operation
              (transaction.typeOperation && transaction.typeOperation.toLowerCase().includes(query)) ||
              // Recherche dans le moyen de paiement
              (transaction.paiement && transaction.paiement.toLowerCase().includes(query)) ||
              // Recherche dans le statut
              (transaction.statut && transaction.statut.toLowerCase().includes(query)) ||
              // Recherche dans les informations supplementaires
              (transaction.infos && transaction.infos.toLowerCase().includes(query)) ||
              // Recherche dans l'origine
              (transaction.origine && transaction.origine.toLowerCase().includes(query)) ||
              // Recherche dans le niveau (formaté)
              (transaction.niveau && this.formatNiveau(transaction.niveau).toLowerCase().includes(query))
          );
        });
        console.log(`🔍 Transaction search filter: ${beforeFilter} -> ${filtered.length} transactions`);
      }

      console.log("✅ Final filtered transactions:", filtered);
      return filtered;
    },

    transactionDetailsHtml() {
      if (!this.selectedTransaction) return "";

      const t = this.selectedTransaction;
      return `
    <strong style="color: #1976D2;">🏢 Entreprise</strong><br>
    ${t.entreprise}<br><br>

    <strong style="color: #1976D2;">👤 Employe</strong><br>
    ${t.nom} ${t.prenom} (${t.matricule})<br><br>

    <strong style="color: #1976D2;">📅 Date Commande</strong><br>
    ${this.convertDate(t.dateCommande)}<br><br>

    <strong style="color: #1976D2;">🕒 Date Transaction</strong><br>
    ${this.datefull(t.dateTransaction)}<br><br>

    <strong style="color: #1976D2;">💰 Montant</strong><br>
    <span style="font-size: 16px; font-weight: bold; color: #E65100;">${t.montant} FCFA</span><br><br>

    <strong style="color: #1976D2;">🔧 Type Operation</strong><br>
    ${t.typeOperation}<br><br>

    <strong style="color: #1976D2;">💳 Moyen de Paiement</strong><br>
    ${t.paiement}<br><br>

    <strong style="color: #1976D2;">✅ Statut</strong><br>
    <span style="color: ${this.getStatusColorHex(t.statut)}; font-weight: bold;">${t.statut}</span><br><br>

    <strong style="color: #1976D2;">📊 Niveau</strong><br>
    ${this.formatNiveau(t.niveau)}<br><br>

    <strong style="color: #1976D2;">🌐 Origine</strong><br>
    ${t.origine || 'N/A'}<br><br>

    ${t.infos && t.infos !== 'null' ? `<strong style="color: #1976D2;">ℹ️ Informations</strong><br>${t.infos}<br><br>` : ''}

    <hr style="margin: 15px 0;">
    <small style="color: #757575;">
      Transaction ID: ${t.transactionUid}<br>
      Reference: #${t.id}
    </small>
  `;
    }
  },

  created() {
    this.balanceInfos.matricule = sessionStorage.getItem("userId");
    this.balanceInfos.companyName = sessionStorage.getItem("companyName");
    // Enhanced initialization for recharges
    this.updateDateRangeRecharges(this.selectedPeriodRecharges);
    // NOUVEAU: Initialisation pour paiements externes
    this.updateDateRangeExternal(this.selectedPeriodExternal);
  },

  mounted() {
    moment.locale('fr');
    this.loadData();
  },

  methods: {
    formatDateFR(date) {
      if (!date) return '';
      return moment(date).format('DD/MM/YYYY');
    },

    calculateRechargeStatsFromTransactions() {
      console.log("=== DEBUGGING CALCUL STATS RECHARGES ===");
      console.log("📅 Période actuelle:", this.selectedPeriodRecharges);
      console.log("📅 Date début:", this.dateDebutRecharges);
      console.log("📅 Date fin:", this.dateFinRecharges);
      console.log("📊 Recharges brutes:", this.recharges.length);

      // 🔧 CORRIGÉ : Calculer sur données brutes avec SEULEMENT filtre de dates
      const rechargesToAnalyze = this.recharges.filter(r => {
        if (!r.dateTransaction) return false;

        const transDate = moment(r.dateTransaction);
        if (!transDate.isValid()) return false;

        return transDate.isSameOrAfter(this.dateDebutRecharges, 'day') &&
            transDate.isSameOrBefore(this.dateFinRecharges, 'day');
      });

      console.log("📊 Recharges filtrées (dates seulement):", rechargesToAnalyze.length);

      // 🆕 VALIDATION : Vérifier cohérence API vs Calcul dynamique
      if (this.rechargeStats && rechargesToAnalyze.length > 0) {
        const dynamicTotal = rechargesToAnalyze
            .filter(r => r.typeOperation === 'CREDIT')
            .reduce((sum, r) => sum + (parseInt(r.amount) || 0), 0);

        console.log("💰 Comparaison totaux:");
        console.log("  - API synchronisé:", this.rechargeStats.totalCredits || 0);
        console.log("  - Calcul dynamique:", dynamicTotal);

        const difference = Math.abs((this.rechargeStats.totalCredits || 0) - dynamicTotal);
        if (difference > 0) {
          console.warn("⚠️ DIFFÉRENCE détectée:", difference, "FCFA");
          console.log("Possible cause: Délai de synchronisation ou filtres différents");
        } else {
          console.log("✅ Totaux cohérents - Synchronisation réussie");
        }
      }

      if (!rechargesToAnalyze || rechargesToAnalyze.length === 0) {
        this.rechargeStats = {
          totalCredits: 0,
          countCredits: 0,
          totalDebits: 0,
          countDebits: 0,
          lastRecharge: null
        };
        console.log("📊 Stats vides - Aucune recharge filtrée");
        return;
      }

      const stats = {
        totalCredits: 0,
        countCredits: 0,
        totalDebits: 0,
        countDebits: 0,
        lastRecharge: null
      };

      const sortedRecharges = [...rechargesToAnalyze].sort((a, b) =>
          moment(b.dateTransaction).valueOf() - moment(a.dateTransaction).valueOf()
      );

      if (sortedRecharges.length > 0) {
        stats.lastRecharge = {
          date: sortedRecharges[0].dateTransaction,
          amount: sortedRecharges[0].amount
        };
      }

      // Détail des recharges filtrées pour debugging
      console.log("📋 Détail des recharges filtrées:");
      rechargesToAnalyze.forEach((r, index) => {
        console.log(`${index + 1}. ${r.dateTransaction} - ${r.typeOperation} - ${r.amount} FCFA`);
      });

      rechargesToAnalyze.forEach(recharge => {
        const amount = parseInt(recharge.amount) || 0;

        if (recharge.typeOperation === 'CREDIT') {
          stats.totalCredits += amount;
          stats.countCredits++;
        } else if (recharge.typeOperation === 'DEBIT') {
          stats.totalDebits += amount;
          stats.countDebits++;
        }
      });

      // 🔧 CORRIGÉ : Mettre à jour rechargeStats avec le calcul dynamique
      this.rechargeStats = {
        ...this.rechargeStats,  // Garder les autres propriétés de l'API
        totalCredits: stats.totalCredits,
        countCredits: stats.countCredits,
        totalDebits: stats.totalDebits,
        countDebits: stats.countDebits,
        lastRecharge: stats.lastRecharge
      };

      console.log("📊 Stats calculées (comparaison seulement):", stats);
      console.log("📊 Stats API (utilisées pour affichage):", this.rechargeStats);
      console.log("===================================");
    },

    calculateExternalStatsFromTransactions() {
      // Utiliser les paiements filtrés au lieu de tous les paiements
      const paymentsToAnalyze = this.filteredExternalPayments || [];

      console.log("📊 Calculating external stats from", paymentsToAnalyze.length, "filtered payments");

      if (!paymentsToAnalyze || paymentsToAnalyze.length === 0) {
        this.externalStats = {
          totalAmount: 0,
          totalCount: 0,
          successCount: 0,
          successAmount: 0,
          pendingCount: 0,
          pendingAmount: 0,
          failedCount: 0,
          failedAmount: 0,
          lastPayment: null
        };
        return;
      }

      const stats = {
        totalAmount: 0,
        totalCount: paymentsToAnalyze.length,
        successCount: 0,
        successAmount: 0,
        pendingCount: 0,
        pendingAmount: 0,
        failedCount: 0,
        failedAmount: 0,
        lastPayment: null
      };

      const sortedPayments = [...paymentsToAnalyze].sort((a, b) =>
          moment(b.transactionDate).valueOf() - moment(a.transactionDate).valueOf()
      );

      if (sortedPayments.length > 0) {
        stats.lastPayment = {
          date: sortedPayments[0].transactionDate,
          amount: sortedPayments[0].amount
        };
      }

      paymentsToAnalyze.forEach(payment => {
        const amount = parseInt(payment.amount) || 0;
        stats.totalAmount += amount;

        switch (payment.status) {
          case 'SUCCESS':
            stats.successCount++;
            stats.successAmount += amount;
            break;
          case 'PENDING':
            stats.pendingCount++;
            stats.pendingAmount += amount;
            break;
          case 'FAILED':
          case 'CANCELLED':
            stats.failedCount++;
            stats.failedAmount += amount;
            break;
        }
      });

      this.externalStats = stats;
      console.log("📊 External stats updated from filtered data:", this.externalStats);
    },

    // NOUVEAU: Méthodes pour paiements externes
    async loadExternalPayments() {
      console.log("🚀 Loading external payments for restaurant...");
      console.log("Restaurant:", this.infosEnterpriseReference.nom);
      console.log("Restaurant ID:", this.infosEnterpriseReference.id);

      this.loadingExternal = true;

      try {
        await Promise.allSettled([
          this.getExternalPayments(),
          this.getExternalPaymentStatistics()
        ]);

        console.log("✅ External payments loaded successfully for restaurant");

        if (this.externalPayments.length === 0) {
          console.log("ℹ️ No external payment history found for this restaurant");
          this.showAlert("info", "Information",
              "Aucun historique de paiement externe trouvé pour ce restaurant.");
        }

      } catch (error) {
        console.error("❌ Error during external payments loading:", error);
        this.showAlert("error", "Erreur", "Impossible de charger l'historique des paiements externes");
      } finally {
        this.loadingExternal = false;
        console.log("🏁 External payments loading finished");
      }
    },

    // NOUVEAU: Get external payments for all employees
    async getExternalPayments() {
      const startDate = moment().subtract(6, "months").format("YYYY-MM-DD");
      const endDate = moment().add(1, "day").format("YYYY-MM-DD");

      try {
        // Get list of unique employee IDs from existing transactions
        const employeeIds = [...new Set(this.transactions.map(t => t.matricule))];

        if (employeeIds.length === 0) {
          console.log("⚠️ No employee IDs found in transactions");
          this.externalPayments = [];
          return;
        }

        console.log("👥 Found", employeeIds.length, "unique employees for external payments:", employeeIds);

        // Fetch external payments for each employee
        const allExternalPayments = [];

        for (const matricule of employeeIds) {
          try {
            const url = `${this.$store.getters.user.baseUrl}/api/external-payments/history/employee/${matricule}/${sessionStorage.getItem("companyId")}`;

            console.log(`📞 Fetching external payments for employee ${matricule}...`);

            const response = await apiClient.get(url, {
              params: {startDate, endDate}
            });

            console.log(`📊 External payments response for ${matricule}:`, response.data);

            if (response.data && Array.isArray(response.data)) {
              const employeeExternalPayments = response.data.map(item => ({
                id: item.id,
                transactionUid: item.transactionUid,
                transactionDate: item.transactionDate,
                amount: item.amount,
                currency: item.currency || 'XOF',
                paymentMethod: item.paymentMethod ? item.paymentMethod.name : 'N/A',
                status: item.status,
                typeAuth: item.typeAuth || 'N/A',
                description: item.description || 'N/A',
                externalReference: item.externalReference || '',
                customerType: item.account ? 'Identifié' : 'Anonyme',
                customerName: item.account ?
                    item.displayCustomerName || `${item.account.accountFirstname} ${item.account.accountLastname}` :
                    item.anonymousCustomerName || 'Client anonyme',
                customerPhone: item.account ?
                    item.account.accountPhonenumber || 'N/A' :
                    item.anonymousCustomerPhone || 'N/A',
                customerEmail: item.account ?
                    item.displayCustomerEmail || item.account.accountEmail :
                    item.anonymousCustomerEmail || 'N/A',
                origin: item.origin || 'N/A',
                processedBy: item.processedBy || 'N/A',
                notes: item.notes || '',
                failureReason: item.failureReason || '',
                creationDate: item.creationDate,
                revisedDate: item.revisedDate,
                paymentMethodColor: item.paymentMethod ? item.paymentMethod.color : null,
                paymentMethodIcon: item.paymentMethod ? item.paymentMethod.icon : null,
                paymentMethodDescription: item.paymentMethod ? item.paymentMethod.description : null,
                isAnonymous: item.anonymousTransaction || false,
                accountInfo: item.account || null,
                // CORRIGÉ : Chercher directement dans les transactions
                employeeId: matricule,
                employeeFirstname: this.getEmployeeInfoFromTransactions(matricule, 'prenom'),
                employeeLastname: this.getEmployeeInfoFromTransactions(matricule, 'nom')
              }));

              allExternalPayments.push(...employeeExternalPayments);
              console.log(`✅ Added ${employeeExternalPayments.length} external payments for ${matricule}`);
            }
          } catch (employeeError) {
            console.warn(`⚠️ Error for employee ${matricule}:`, employeeError);
            // Continue with other employees
          }
        }

        this.externalPayments = allExternalPayments;
        console.log("📋 Total external payments loaded:", this.externalPayments.length);

        // Extract unique payment methods
        const paymentMethods = [...new Set(this.externalPayments.map(p => p.paymentMethod))];
        this.paymentMethodOptions = ["Tous", ...paymentMethods];

      } catch (error) {
        console.error("❌ Error loading external payments:", error);
        this.externalPayments = [];
      }
    },

    getEmployeeInfoFromTransactions(matricule, field) {
      const transaction = this.transactions.find(t => t.matricule === matricule);
      return transaction ? transaction[field] : 'N/A';
    },

    // NOUVEAU: Get external payment statistics
    async getExternalPaymentStatistics() {
      try {
        if (this.externalPayments.length === 0) {
          this.externalStats = {
            totalAmount: 0,
            totalCount: 0,
            successCount: 0,
            successAmount: 0,
            pendingCount: 0,
            pendingAmount: 0,
            failedCount: 0,
            failedAmount: 0,
            lastPayment: null
          };
          return;
        }

        const stats = {
          totalAmount: 0,
          totalCount: this.externalPayments.length,
          successCount: 0,
          successAmount: 0,
          pendingCount: 0,
          pendingAmount: 0,
          failedCount: 0,
          failedAmount: 0,
          lastPayment: null
        };

        // Sort by date to find last payment
        const sortedPayments = [...this.externalPayments].sort((a, b) =>
            moment(b.transactionDate).valueOf() - moment(a.transactionDate).valueOf()
        );

        if (sortedPayments.length > 0) {
          stats.lastPayment = {
            date: sortedPayments[0].transactionDate,
            amount: sortedPayments[0].amount
          };
        }

        // Calculate statistics by status
        this.externalPayments.forEach(payment => {
          const amount = parseInt(payment.amount) || 0;
          stats.totalAmount += amount;

          switch (payment.status) {
            case 'SUCCESS':
              stats.successCount++;
              stats.successAmount += amount;
              break;
            case 'PENDING':
              stats.pendingCount++;
              stats.pendingAmount += amount;
              break;
            case 'FAILED':
            case 'CANCELLED':
              stats.failedCount++;
              stats.failedAmount += amount;
              break;
          }
        });

        this.externalStats = stats;
        console.log("📊 External payment statistics calculated:", this.externalStats);

      } catch (error) {
        console.error("❌ Error calculating external payment statistics:", error);
        this.externalStats = {
          totalAmount: 0,
          totalCount: 0,
          successCount: 0,
          successAmount: 0,
          pendingCount: 0,
          pendingAmount: 0,
          failedCount: 0,
          failedAmount: 0,
          lastPayment: null
        };
      }
    },

    // NOUVEAU: Update date range for external payments
    updateDateRangeExternal(period) {
      switch (period) {
        case 'today':
          this.dateDebutExternal = moment().format('YYYY-MM-DD');
          this.dateFinExternal = moment().format('YYYY-MM-DD');
          break;
        case 'week':
          this.dateDebutExternal = moment().startOf('isoWeek').format('YYYY-MM-DD');
          this.dateFinExternal = moment().endOf('isoWeek').format('YYYY-MM-DD');
          break;
        case 'month':
          this.dateDebutExternal = moment().startOf('month').format('YYYY-MM-DD');
          this.dateFinExternal = moment().endOf('month').format('YYYY-MM-DD');
          break;
        case 'last_month':
          this.dateDebutExternal = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.dateFinExternal = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
          break;
      }
    },

    // NOUVEAU: External payment utility methods
    formatExternalDate(date) {
      if (!date || date === "0") return "N/A";

      let parsedDate;
      if (typeof date === 'string' && date.includes(' ')) {
        parsedDate = moment(date, "YYYY-MM-DD HH:mm:ss");
      } else {
        parsedDate = moment(date);
      }

      return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY HH:mm") : "Date invalide";
    },

    formatExternalAmount(amount) {
      if (!amount || amount === 0) return "0 FCFA";
      return new Intl.NumberFormat('fr-FR').format(amount) + " FCFA";
    },

    parseExternalDate(date) {
      if (!date) return null;

      if (typeof date === 'string' && date.includes(' ')) {
        return moment(date, "YYYY-MM-DD HH:mm:ss");
      } else {
        return moment(date);
      }
    },

    getPaymentMethodColor(method, item = null) {
      if (item && item.paymentMethodColor) {
        return item.paymentMethodColor;
      }

      const colors = {
        "Cash": "green",
        "Wave": "blue",
        "Orange Money": "orange",
        "Carte Bancaire": "purple",
        "Espèces": "green",
        "Autre": "grey"
      };
      return colors[method] || "grey";
    },

    getPaymentMethodIcon(method, item = null) {
      if (item && item.paymentMethodIcon) {
        return `mdi-${item.paymentMethodIcon}`;
      }

      const icons = {
        "Cash": "mdi-cash",
        "Wave": "mdi-water",
        "Orange Money": "mdi-phone",
        "Carte Bancaire": "mdi-credit-card",
        "Espèces": "mdi-cash",
        "Autre": "mdi-wallet"
      };
      return icons[method] || "mdi-wallet";
    },

    getExternalStatusColor(status) {
      const colors = {
        "SUCCESS": "success",
        "FAILED": "error",
        "CANCELLED": "warning",
        "PENDING": "info"
      };
      return colors[status] || "grey";
    },

    getExternalStatusIcon(status) {
      const icons = {
        "SUCCESS": "mdi-check",
        "FAILED": "mdi-close",
        "CANCELLED": "mdi-cancel",
        "PENDING": "mdi-clock"
      };
      return icons[status] || "mdi-help";
    },

    getExternalStatValue(statKey) {
      if (!this.externalStats) return "0";

      const value = this.externalStats[statKey];
      if (value === null || value === undefined) return "0";

      if (statKey.includes('Amount')) {
        return this.formatNumber(value);
      }

      return value.toString();
    },

    getLastExternalPaymentDate() {
      if (!this.externalStats || !this.externalStats.lastPayment) {
        return "Aucun";
      }
      return this.formatExternalDate(this.externalStats.lastPayment.date);
    },

    getLastExternalPaymentAmount() {
      if (!this.externalStats || !this.externalStats.lastPayment) {
        return "Aucun paiement effectué";
      }
      return `${this.externalStats.lastPayment.amount} FCFA`;
    },

    viewDetailsExternal(item) {
      const details = `
      <strong style="color: #FF9800;">👤 Employé</strong><br>
      ${item.employeeLastname} ${item.employeeFirstname} (${item.employeeId})<br><br>

      <strong style="color: #FF9800;">💰 Montant</strong><br>
      <span style="font-size: 18px; font-weight: bold; color: #FF9800;">
        ${this.formatExternalAmount(item.amount)}
      </span><br><br>

      <strong style="color: #FF9800;">📅 Date et Heure</strong><br>
      ${this.formatExternalDate(item.transactionDate)}<br><br>

      <strong style="color: #FF9800;">💳 Méthode de Paiement</strong><br>
      ${item.paymentMethod}<br><br>

      <strong style="color: #FF9800;">👤 Type de Client</strong><br>
      ${item.customerType}<br><br>

      <strong style="color: #FF9800;">👥 Client</strong><br>
      ${item.customerName}<br>
      ${item.customerPhone || 'N/A'}<br><br>

      <strong style="color: #FF9800;">🔐 Authentification</strong><br>
      ${item.typeAuth}<br><br>

      <strong style="color: #FF9800;">✅ Statut</strong><br>
      ${item.status}<br><br>

      <strong style="color: #FF9800;">📝 Description</strong><br>
      ${item.description}<br><br>

      ${item.externalReference ? `<strong style="color: #FF9800;">🔗 Référence Externe</strong><br>${item.externalReference}<br><br>` : ''}

      ${item.failureReason ? `<strong style="color: #F44336;">❌ Raison d'Échec</strong><br>${item.failureReason}<br><br>` : ''}

      ${item.notes ? `<strong style="color: #FF9800;">📋 Notes</strong><br>${item.notes}<br><br>` : ''}

      <hr style="margin: 15px 0;">
      <small style="color: #757575;">
        Transaction ID: ${item.transactionUid}<br>
        Référence: #${item.id}<br>
        Restaurant: ${this.infosEnterpriseReference.nom}<br>
        ${item.processedBy ? `Traité par: ${item.processedBy}` : ''}
      </small>
    `;

      this.detailsExternalDialog = true;
      this.detailsExternalContent = details;
    },

    clearSearchExternal() {
      this.searchQueryExternal = "";
    },

    getNoExternalDataText() {
      if (this.loadingExternal) {
        return "Chargement des paiements externes...";
      }
      if (this.externalPayments.length === 0) {
        return "Aucun paiement externe trouvé";
      }
      return "Aucun paiement externe correspond aux filtres sélectionnés";
    },

    getNoExternalDataTitle() {
      if (this.externalPayments.length === 0) {
        return "Aucun historique de paiement externe";
      }
      return "Aucun résultat";
    },

    getNoExternalDataMessage() {
      if (this.externalPayments.length === 0) {
        return "Aucun paiement externe n'a été effectué par les employés de cette entreprise pour le moment.";
      }
      return "Essayez de modifier vos filtres ou votre période de recherche.";
    },

    // Enhanced no-data handling methods
    getNoDataText() {
      if (this.loadingRecharges) {
        return "Chargement des recharges...";
      }
      if (this.recharges.length === 0) {
        return "Aucune recharge trouvée";
      }
      return "Aucune recharge correspond aux filtres sélectionnés";
    },

    getNoDataTitle() {
      if (this.recharges.length === 0) {
        return "Aucun historique de recharge";
      }
      return "Aucun résultat";
    },

    getNoDataMessage() {
      if (this.recharges.length === 0) {
        return "Les employés de ce restaurant n'ont effectué aucune recharge pour le moment.";
      }
      return "Essayez de modifier vos filtres ou votre période de recherche.";
    },

    // Enhanced statistics methods that handle empty data
    getStatValue(statKey) {
      if (!this.rechargeStats) return "0";

      const value = this.rechargeStats[statKey];
      if (value === null || value === undefined) return "0";

      if (statKey.includes('total') || statKey.includes('Credits') || statKey.includes('Debits')) {
        return this.formatNumber(value);
      }

      return value.toString();
    },

    getLastRechargeDate() {
      if (!this.rechargeStats || !this.rechargeStats.lastRecharge) {
        return "Aucune";
      }
      return this.formatDate(this.rechargeStats.lastRecharge.date);
    },

    getLastRechargeAmount() {
      if (!this.rechargeStats || !this.rechargeStats.lastRecharge) {
        return "Aucune recharge effectuée";
      }
      return `${this.rechargeStats.lastRecharge.amount} FCFA`;
    },

    // Enhanced loadRecharges with comprehensive error handling and debugging
    async loadRecharges() {
      console.log("=== LOADING RECHARGES FOR RESTAURANT ===");
      console.log("Restaurant:", this.infosEnterpriseReference.nom);
      console.log("Restaurant ID:", this.infosEnterpriseReference.id);

      this.loadingRecharges = true;

      try {
        await Promise.allSettled([
          this.getRecharges(),
          this.getRechargeStatistics()
        ]);

        console.log("✅ Recharges loaded successfully for restaurant");

        this.$nextTick(() => {
          this.calculateRechargeStatsFromTransactions();
        });

        if (this.recharges.length === 0) {
          console.log("ℹ️ No recharge history found for this restaurant");
          this.showAlert("info", "Information",
              "Aucun historique de recharge trouvé pour ce restaurant.");
        }
      } catch (error) {
        console.error("❌ Error during recharges loading:", error);
        this.showAlert("error", "Erreur", "Impossible de charger l'historique des recharges");
      } finally {
        this.loadingRecharges = false;
        console.log("🏁 Recharges loading finished");

        // Debug the final state
        this.$nextTick(() => {
          this.debugRecharges();
        });
      }
    },

    // Enhanced debugging method
    debugRecharges() {
      console.log("=== RESTAURANT RECHARGES DEBUG ===");
      console.log("Raw recharges:", this.recharges);
      console.log("Recharge stats:", this.rechargeStats);
      console.log("Loading state:", this.loadingRecharges);
      console.log("Restaurant info:", {
        nom: this.infosEnterpriseReference.nom,
        id: this.infosEnterpriseReference.id,
        companyId: sessionStorage.getItem("companyId")
      });
      console.log("Date filters:", {
        dateDebutRecharges: this.dateDebutRecharges,
        dateFinRecharges: this.dateFinRecharges,
        selectedPeriodRecharges: this.selectedPeriodRecharges
      });
      console.log("Other filters:", {
        selectedTypeRecharge: this.selectedTypeRecharge,
        selectedStatusRecharge: this.selectedStatusRecharge,
        searchQueryRecharges: this.searchQueryRecharges
      });
      console.log("Filtered recharges:", this.filteredRecharges);
      console.log("Headers recharges:", this.headersRecharges);
      console.log("=====================================");
    },

    // Enhanced getRecharges method adapted for restaurant context
    async getRecharges() {
      const startDate = moment().subtract(6, "months").format("YYYY-MM-DD");
      const endDate = moment().add(1, "day").format("YYYY-MM-DD");

      // URL adapted for restaurant context
      const url = `${this.$store.getters.user.baseUrl}/api/wallet/recharge/history/restaurant/${sessionStorage.getItem("companyId")}/${this.infosEnterpriseReference.id}`;

      try {
        console.log("=== RESTAURANT RECHARGES API CALL ===");
        console.log("URL:", url);
        console.log("Params:", {
          startDate: startDate,
          endDate: endDate,
          requesterId: sessionStorage.getItem("accountId")
        });

        const response = await apiClient.get(url, {
          params: {
            startDate: startDate,
            endDate: endDate,
            requesterId: sessionStorage.getItem("accountId")
          }
        });

        console.log("📊 Raw restaurant recharges API response:", response.data);
        console.log("🔍 STRUCTURE COMPLÈTE du premier élément:", JSON.stringify(response.data[0], null, 2));

        if (response.data && Array.isArray(response.data)) {
          console.log("✅ Recharges data is array, length:", response.data.length);

          this.recharges = response.data.map(item => {
            // ANCIEN CODE supprimé - plus besoin de debug ou helper methods

            return {
              id: item.id,
              dateTransaction: item.dateTransaction,
              amount: item.amount,
              typeOperation: item.typeOperation,
              typeAuth: item.typeAuth,
              level: item.level,
              status: item.status,
              reason: item.reason || 'N/A',
              motifFailed: item.motifFailed,
              transactionUid: item.transactionUid,
              balanceBefore: item.balanceBefore,
              balanceAfter: item.balanceAfter,
              origin: item.origin,
              senderId: item.senderId,
              accountId: item.accountId,
              // NOUVEAU : Utiliser directement les données employee du backend
              employeeId: item.employee?.userId || 'N/A',
              employeeFirstname: item.employee?.accountFirstname || 'N/A',
              employeeLastname: item.employee?.accountLastname || 'N/A',
              employeeEmail: item.employee?.accountEmail || 'N/A'
            };
          });

          console.log("✅ Mapped restaurant recharges:", this.recharges);
        } else if (response.data && response.data.status === 200 && response.data.datas) {
          // Alternative response structure
          console.log("📋 Recharges data in .datas property:", response.data.datas);

          this.recharges = response.data.datas.map(item => ({
            id: item.id,
            dateTransaction: item.dateTransaction,
            amount: item.amount,
            typeOperation: item.typeOperation,
            typeAuth: item.typeAuth,
            level: item.level,
            status: item.status,
            reason: item.reason || 'N/A',
            motifFailed: item.motifFailed,
            transactionUid: item.transactionUid,
            balanceBefore: item.balanceBefore,
            balanceAfter: item.balanceAfter,
            origin: item.origin,
            senderId: item.senderId,
            accountId: item.accountId,
            // CORRIGÉ : Utiliser directement les données employee du backend
            employeeId: item.employee?.userId || 'N/A',
            employeeFirstname: item.employee?.accountFirstname || 'N/A',
            employeeLastname: item.employee?.accountLastname || 'N/A',
            employeeEmail: item.employee?.accountEmail || 'N/A'
          }));

          console.log("✅ Mapped restaurant recharges from .datas:", this.recharges);
        } else {
          console.log("⚠️ Unexpected response structure or empty data:", response.data);
          this.recharges = [];
        }
      } catch (error) {
        console.error("❌ Error loading restaurant recharges:", error);
        console.error("Error details:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        this.recharges = [];
      }
    },

    // Enhanced getRechargeStatistics method
    async getRechargeStatistics() {
      // 🆕 NOUVEAU : Utiliser les mêmes dates que le filtrage dynamique
      const startDate = this.dateDebutRecharges;
      const endDate = this.dateFinRecharges;

      // URL adapted for restaurant context
      const url = `${this.$store.getters.user.baseUrl}/api/wallet/recharge/statistics/restaurant/${sessionStorage.getItem("companyId")}/${this.infosEnterpriseReference.id}`;

      try {
        console.log("=== RESTAURANT RECHARGE STATISTICS API CALL (SYNCHRONIZED) ===");
        console.log("URL:", url);
        console.log("🆕 SYNCHRONIZED Params:", {
          startDate: startDate,
          endDate: endDate,
          requesterId: sessionStorage.getItem("accountId")
        });

        const response = await apiClient.get(url, {
          params: {
            startDate: startDate,    // 🆕 SYNCHRONISÉ avec filtrage
            endDate: endDate,        // 🆕 SYNCHRONISÉ avec filtrage
            requesterId: sessionStorage.getItem("accountId")
          }
        });

        console.log("📊 Restaurant recharge statistics response (SYNCHRONIZED):", response.data);

        if (response.data) {
          this.rechargeStats = response.data;
          console.log("✅ Restaurant recharge stats set (SYNCHRONIZED):", this.rechargeStats);
        }
      } catch (error) {
        console.error("❌ Error loading restaurant recharge statistics:", error);
        console.error("Stats error details:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        // 🆕 FALLBACK : Stats vides si erreur
        this.rechargeStats = {
          totalCredits: 0,
          countCredits: 0,
          lastRecharge: null
        };
      }
    },

    updateDateRangeRecharges(period) {
      moment.locale('fr');

      console.log("🔄 Updating date range for period:", period);

      switch (period) {
        case 'today':
          this.dateDebutRecharges = moment().format('YYYY-MM-DD');
          this.dateFinRecharges = moment().format('YYYY-MM-DD');
          break;
        case 'week': {
          // 🆕 CORRIGÉ : Limiter la semaine aux bornes du mois actuel
          let weekStart = moment().startOf('isoWeek');
          let weekEnd = moment().endOf('isoWeek');
          const monthStart = moment().startOf('month');
          const monthEnd = moment().endOf('month');

          // Limiter la semaine au mois actuel pour éviter les débordements
          if (weekStart.isBefore(monthStart)) {
            console.log("⚠️ Semaine déborde avant le mois, limitation au début du mois");
            weekStart = monthStart;
          }
          if (weekEnd.isAfter(monthEnd)) {
            console.log("⚠️ Semaine déborde après le mois, limitation à la fin du mois");
            weekEnd = monthEnd;
          }

          this.dateDebutRecharges = weekStart.format('YYYY-MM-DD');
          this.dateFinRecharges = weekEnd.format('YYYY-MM-DD');

          console.log("📅 Semaine limitée au mois:", this.dateDebutRecharges, "au", this.dateFinRecharges);
          break;
        }
        case 'month':
          this.dateDebutRecharges = moment().startOf('month').format('YYYY-MM-DD');
          this.dateFinRecharges = moment().endOf('month').format('YYYY-MM-DD');
          break;
        case 'last_month':
          this.dateDebutRecharges = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.dateFinRecharges = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
          break;
        default:
          // Période personnalisée - garder les dates existantes
          break;
      }

      console.log("📅 New date range:", this.dateDebutRecharges, "to", this.dateFinRecharges);

      // 🔧 NOUVEAU : Déclencher rechargement des stats SEULEMENT si on a des données
      if (this.activeTab === 'tab-recharges' && this.recharges.length > 0) {
        this.$nextTick(() => {
          this.calculateRechargeStatsFromTransactions();
        });
      }
    },

    // Enhanced utility methods for recharges
    getTypeOperationColor(type) {
      return type === "CREDIT" ? "success" : "error";
    },

    getTypeOperationIcon(type) {
      return type === "CREDIT" ? "mdi-plus" : "mdi-minus";
    },

    getStatusColorRecharge(status) {
      const colors = {
        "SUCCESS": "success",
        "FAILED": "error",
        "CANCELLED": "warning",
        "PENDING": "info"
      };
      return colors[status] || "grey";
    },

    getStatusIconRecharge(status) {
      const icons = {
        "SUCCESS": "mdi-check",
        "FAILED": "mdi-close",
        "CANCELLED": "mdi-cancel",
        "PENDING": "mdi-clock"
      };
      return icons[status] || "mdi-help";
    },

    getAmountClass(typeOperation) {
      return {
        'font-weight-bold': true,
        'success--text': typeOperation === 'CREDIT',
        'error--text': typeOperation === 'DEBIT'
      };
    },

    getAuthColor(typeAuth) {
      const colors = {
        "MANUEL": "primary",
        "CARTE": "info",
        "BIO": "purple",
        "AUTOMATIC": "orange"
      };
      return colors[typeAuth] || "grey";
    },

    // Enhanced viewDetailsRecharge method
    viewDetailsRecharge(item) {
      const details = `
      <strong style="color: #4CAF50;">👤 Employé</strong><br>
      ${item.employeeLastname} ${item.employeeFirstname} (${item.employeeId})<br><br>

      <strong style="color: #4CAF50;">💰 Montant</strong><br>
      <span style="font-size: 18px; font-weight: bold; color: ${item.typeOperation === 'CREDIT' ? '#4CAF50' : '#F44336'};">
        ${item.typeOperation === 'DEBIT' ? '-' : '+'}${item.amount} FCFA
      </span><br><br>

      <strong style="color: #4CAF50;">📅 Date et Heure</strong><br>
      ${this.formatDate(item.dateTransaction)}<br><br>

      <strong style="color: #4CAF50;">🔄 Type d'Opération</strong><br>
      ${item.typeOperation}<br><br>

      <strong style="color: #4CAF50;">🔐 Authentification</strong><br>
      ${item.typeAuth}<br><br>

      <strong style="color: #4CAF50;">📊 Niveau</strong><br>
      ${this.formatNiveau(item.level)}<br><br>

      <strong style="color: #4CAF50;">✅ Statut</strong><br>
      ${item.status}<br><br>

      <strong style="color: #4CAF50;">📝 Raison</strong><br>
      ${item.reason}<br><br>

      <strong style="color: #4CAF50;">💳 Soldes</strong><br>
      Avant: ${item.balanceBefore} FCFA<br>
      Après: ${item.balanceAfter} FCFA<br><br>

      <hr style="margin: 15px 0;">
      <small style="color: #757575;">
        Transaction ID: ${item.transactionUid}<br>
        Référence: #${item.id}<br>
        Restaurant: ${this.infosEnterpriseReference.nom}
      </small>
    `;

      this.detailsRechargeDialog = true;
      this.detailsRechargeContent = details;
    },

    clearSearchRecharges() {
      this.searchQueryRecharges = "";
    },

    canCancelRecharge(item) {
      return sessionStorage.getItem("userId")?.toLowerCase() === "admin" &&
          item.status === "SUCCESS" &&
          moment().diff(moment(item.dateTransaction), 'hours') <= 24;
    },

    // Enhanced cancelRecharge method
    async cancelRecharge(item) {
      if (!this.canCancelRecharge(item)) {
        this.showAlert("error", "Action non autorisée", "Impossible d'annuler cette recharge.");
        return;
      }

      const reason = prompt("Raison de l'annulation:");
      if (!reason) return;

      this.loadingRecharges = true;
      try {
        const url = `${this.$store.getters.user.baseUrl}/api/wallet/recharge/cancel/${item.transactionUid}`;
        const response = await apiClient.post(url, null, {
          params: {reason: reason}
        });

        if (response.data.status === 200) {
          this.showAlert("success", "Succès", "Recharge annulée avec succès");
          await this.loadRecharges();
        } else {
          this.showAlert("error", "Erreur", response.data.message);
        }
      } catch (error) {
        console.error("❌ Error canceling recharge:", error);
        this.showAlert("error", "Erreur", "Impossible d'annuler la recharge");
      } finally {
        this.loadingRecharges = false;
      }
    },

    // Enhanced formatDate method
    formatDate(date) {
      if (!date || date === "0") return "N/A";
      return moment(date).format("DD-MM-YYYY HH:mm");
    },

    // Enhanced refreshData with recharge and external payment support
    async refreshData() {
      try {
        if (this.activeTab === 'tab-recharges') {
          await this.loadRecharges();
          // 🆕 NOUVEAU: Recharger les stats avec la période courante
          await this.getRechargeStatistics();
          // 🆕 NOUVEAU: Recalculer les stats après chargement
          this.$nextTick(() => {
            this.calculateRechargeStatsFromTransactions();
          });
        } else if (this.activeTab === 'tab-paiements-externes') {
          await this.loadExternalPayments();
          // NOUVEAU : Recalculer les stats après le chargement
          this.$nextTick(() => {
            this.calculateExternalStatsFromTransactions();
          });
        }

        this.searchName = "";
        this.depthVal = {name: "Profondeur Hier", value: "1"};
        this.heightVal = {name: "Hauteur Demain", value: "1"};

        // Reset recharge filters
        this.selectedPeriodRecharges = "month";
        this.selectedTypeRecharge = "Tous";
        this.selectedStatusRecharge = "Tous";
        this.searchQueryRecharges = "";
        this.updateDateRangeRecharges("month");

        // Reset external filters
        this.selectedPeriodExternal = "month";
        this.selectedPaymentMethod = "Tous";
        this.selectedStatusExternal = "Tous";
        this.searchQueryExternal = "";
        this.updateDateRangeExternal("month");

        await this.loadData();

        // Show appropriate success message
        let message = "Les données ont été mises à jour";
        if (this.activeTab === 'tab-recharges' && this.recharges.length === 0) {
          message = "Données actualisées. Aucun historique de recharge disponible pour ce restaurant.";
        } else if (this.activeTab === 'tab-paiements-externes' && this.externalPayments.length === 0) {
          message = "Données actualisées. Aucun historique de paiement externe disponible pour ce restaurant.";
        }

        this.showAlert("success", "Actualisation réussie", message);
        this.$vuetify.goTo(0);

      } catch (error) {
        console.error("❌ Error during refresh:", error);
        this.showAlert("error", "Erreur", "Erreur lors de l'actualisation des données");
      }
    },

    async loadData() {
      this.loading = true;

      try {
        await this.getInfosEnterpriseReference();
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        this.showAlert("error", "Erreur de chargement", "Impossible de charger les données");
      } finally {
        this.loading = false;
      }
    },

    async getInfosEnterpriseReference() {
      const url = `${this.$store.getters.user.baseUrl}/wallet/get_wallet_company_by_name/${this.infosEnterpriseReference.nom}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);
        if (response.data.status === 200) {
          const data = response.data.datas;
          this.infosEnterpriseReference = {
            id: data.id,
            nom: data.name,
            address: data.address,
            phone: data.phone
          };
          await this.getData();
        } else {
          this.showAlert("warning", "Avertissement", response.data.message);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des infos entreprise:", error);
      }
    },

    async getData() {
      const endDate = moment().add(this.heightVal.value, "days").format("YYYY-MM-DD");
      const startDate = moment().subtract(this.depthVal.value, "days").format("YYYY-MM-DD");

      const url = `${this.$store.getters.user.baseUrl}/wallet/get_wallet_restaurant_transactions_by_company_all/${startDate}/${endDate}/${sessionStorage.getItem("companyId")}/${this.infosEnterpriseReference.id}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);
        if (response.data.status === 200) {
          const data = response.data.datas || [];

          this.transactions = data.map(item => ({
            id: item.id,
            entreprise: this.infosEnterpriseReference.nom,
            nom: item.employee.accountLastname,
            prenom: item.employee.accountFirstname,
            matricule: item.employee.userId,
            dateCommande: item.dateCommand,
            montant: item.price,
            typeOperation: item.typeTrs,
            paiement: item.typeAuth,
            statut: item.status,
            infos: item.infos,
            origine: item.orderOrigin,
            niveau: item.level,
            motifRefus: item.motifFailed,
            dateTransaction: item.dateTransaction,
            transactionUid: item.transaction_uid
          }));

          await this.getFinancialInfosByCompany();
        } else {
          this.showAlert("warning", "Avertissement", response.data.message);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des transactions:", error);
      }
    },

    async getFinancialInfosByCompany() {
      const url = `${this.$store.getters.user.baseUrl}/wallet/get_financial_infos_company/${sessionStorage.getItem("companyId")}/${this.infosEnterpriseReference.id}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);
        if (response.data.status === 200) {
          const data = response.data.datas;

          // Update financial data
          Object.assign(this.financialDetails, data);

          // Update balances
          this.balanceInfos.globalRevenueActualMonth = data.globalRevenueActualMonth;
          this.balanceInfos.amountDueByCompanyActualMonth = data.amountDueByCompanyActualMonth;
          this.balanceInfos.amountAlreadyPaidByEmployeeActualMonth = data.amountAlreadyPaidByEmployeeActualMonth;
          this.balanceInfos.globalRevenuePreviousMonth = data.globalRevenuePreviousMonth;
          this.balanceInfos.amountDueByCompanyPreviousMonth = data.amountDueByCompanyPreviousMonth;
          this.balanceInfos.amountAlreadyPaidByEmployeePreviousMonth = data.amountAlreadyPaidByEmployeePreviousMonth;

          // Enhanced: After getting financial info, get unit price
          this.getUnitPriceTransaction();
        }
      } catch (error) {
        console.error("Erreur lors du chargement des infos financières:", error);
      }
    },

// Méthode pour obtenir le texte de tendance
    getTrendDescription() {
      if (this.personalTransactionsStats.currentMonthTotal > this.personalTransactionsStats.previousMonthTotal) {
        return 'Hausse des achats personnels';
      } else if (this.personalTransactionsStats.currentMonthTotal < this.personalTransactionsStats.previousMonthTotal) {
        return 'Baisse des achats personnels';
      } else {
        return 'Niveau stable';
      }
    },

    /**
     * Enhanced method for getting personal transactions stats
     */
    async getPersonalTransactionsStats() {
      this.personalTransactionsStats.loading = true;
      this.personalTransactionsStats.error = null;

      // NOUVEAU : Utiliser l'endpoint modifié qui inclut wallet + external
      const url = `${this.$store.getters.user.baseUrl}/wallet/personal-transactions/stats/${this.infosEnterpriseReference.id}/${sessionStorage.getItem("accountId")}`;

      try {
        const response = await apiClient.get(url);

        if (response.status === 200) {
          const data = response.data.datas;

          console.log("📊 Personal transaction stats received:", data);

          // NOUVEAU : Mapper les nouvelles propriétés du DTO enrichi
          this.personalTransactionsStats.currentMonthWallet = data.currentMonthWallet || 0;
          this.personalTransactionsStats.currentMonthExternal = data.currentMonthExternal || 0;
          this.personalTransactionsStats.currentMonthTotal = data.currentMonthTotal || 0;

          this.personalTransactionsStats.previousMonthWallet = data.previousMonthWallet || 0;
          this.personalTransactionsStats.previousMonthExternal = data.previousMonthExternal || 0;
          this.personalTransactionsStats.previousMonthTotal = data.previousMonthTotal || 0;

          this.personalTransactionsStats.difference = data.difference || 0;
          this.personalTransactionsStats.percentageChange = data.percentageChange || 0;

          // NOUVEAU : Calculer les pourcentages et taux de croissance
          this.calculateEnhancedStats(data);

          // NOUVEAU : Calculer les montants pour l'affichage
          this.calculatePersonalTransactionsAmounts();

          console.log("✅ Enhanced personal transaction statistics loaded:", this.personalTransactionsStats);

        } else {
          this.personalTransactionsStats.error = response.data.message || "Erreur lors du chargement";
          this.showAlert("warning", "Avertissement", response.data.message);
        }

      } catch (error) {
        console.error("❌ Error loading personal transactions stats:", error);
        this.personalTransactionsStats.error = "Erreur de connexion";
        this.showAlert("error", "Erreur", "Impossible de charger les statistiques des transactions personnelles");
      } finally {
        this.personalTransactionsStats.loading = false;
      }
    },

    calculateEnhancedStats(data) {
      // Pourcentages de répartition pour le mois actuel
      if (this.personalTransactionsStats.currentMonthTotal > 0) {
        this.personalTransactionsStats.walletPercentage =
            (this.personalTransactionsStats.currentMonthWallet / this.personalTransactionsStats.currentMonthTotal * 100);
        this.personalTransactionsStats.externalPercentage =
            (this.personalTransactionsStats.currentMonthExternal / this.personalTransactionsStats.currentMonthTotal * 100);
      } else {
        this.personalTransactionsStats.walletPercentage = 0;
        this.personalTransactionsStats.externalPercentage = 0;
      }

      // Taux de croissance spécifique aux transactions externes
      if (this.personalTransactionsStats.previousMonthExternal > 0) {
        const externalDiff = this.personalTransactionsStats.currentMonthExternal - this.personalTransactionsStats.previousMonthExternal;
        this.personalTransactionsStats.externalGrowthRate =
            (externalDiff / this.personalTransactionsStats.previousMonthExternal * 100);
      } else {
        this.personalTransactionsStats.externalGrowthRate = 0;
      }

      console.log("📈 Enhanced stats calculated:", {
        walletPercentage: this.personalTransactionsStats.walletPercentage,
        externalPercentage: this.personalTransactionsStats.externalPercentage,
        externalGrowthRate: this.personalTransactionsStats.externalGrowthRate
      });
    },

    /**
     * Enhanced method: Calculate personal transaction amounts
     */
    calculatePersonalTransactionsAmounts() {
      // Montants pour le mois actuel
      const actualAmountWallet = this.personalTransactionsStats.currentMonthWallet * this.unitPriceTransaction;
      const actualAmountExternal = this.personalTransactionsStats.currentMonthExternal * this.unitPriceTransaction;
      const actualAmountTotal = this.personalTransactionsStats.currentMonthTotal * this.unitPriceTransaction;

      // Montants pour le mois précédent
      const previousAmountWallet = this.personalTransactionsStats.previousMonthWallet * this.unitPriceTransaction;
      const previousAmountExternal = this.personalTransactionsStats.previousMonthExternal * this.unitPriceTransaction;
      const previousAmountTotal = this.personalTransactionsStats.previousMonthTotal * this.unitPriceTransaction;

      // Mise à jour des montants d'affichage
      this.sensNumerikInfos.actualMonthEncours = this.formatNumberWithCommas(actualAmountTotal) + " F CFA";
      this.sensNumerikInfos.previousMonthEncours = this.formatNumberWithCommas(previousAmountTotal) + " F CFA";

      // NOUVEAU : Détails de répartition
      this.personalTransactionsStats.breakdown.wallet.currentAmount = actualAmountWallet;
      this.personalTransactionsStats.breakdown.wallet.previousAmount = previousAmountWallet;
      this.personalTransactionsStats.breakdown.external.currentAmount = actualAmountExternal;
      this.personalTransactionsStats.breakdown.external.previousAmount = previousAmountExternal;

      console.log("💰 Enhanced amounts calculated:", {
        wallet: {current: actualAmountWallet, previous: previousAmountWallet},
        external: {current: actualAmountExternal, previous: previousAmountExternal},
        total: {current: actualAmountTotal, previous: previousAmountTotal}
      });
    },

// Retourne la couleur selon la tendance des transactions externes
    getExternalTrendColor() {
      if (this.personalTransactionsStats.externalGrowthRate > 0) return "success";
      if (this.personalTransactionsStats.externalGrowthRate < 0) return "error";
      return "grey";
    },

// Retourne l'icône selon la tendance des transactions externes
    getExternalTrendIcon() {
      if (this.personalTransactionsStats.externalGrowthRate > 0) return "mdi-trending-up";
      if (this.personalTransactionsStats.externalGrowthRate < 0) return "mdi-trending-down";
      return "mdi-minus";
    },

// Formate le pourcentage avec signe
    formatPercentageWithSign(value) {
      if (value === 0) return "0%";
      return value > 0 ? `+${value.toFixed(1)}%` : `${value.toFixed(1)}%`;
    },

// Retourne les informations de répartition formatées
    getBreakdownInfo(type, period) {
      const breakdown = this.personalTransactionsStats.breakdown[type];
      const amount = breakdown[`${period}Amount`];
      return this.formatNumberWithCommas(amount) + " F CFA";
    },

// Surcharge de formatDifference pour accepter une valeur
    formatDifference(value = null) {
      const diff = value !== null ? value : this.personalTransactionsStats.difference;
      if (diff === 0) return "0";
      return diff > 0 ? `+${this.formatNumberWithCommas(Math.abs(diff))}` : `-${this.formatNumberWithCommas(Math.abs(diff))}`;
    },

    // Enhanced method: Get unit price and calculate amounts
    getUnitPriceTransaction() {
      let str = `${this.$store.getters.user.baseUrl}/wallet/get_unit_price_transaction_resto/${sessionStorage.getItem("companyId")}/${sessionStorage.getItem("accountId")}`;

      apiClient
          .get(str)
          .then(response => {
            if (response.data.status === 200) {
              this.unitPriceTransaction = response.data.datas;

              // Enhanced: After getting unit price, load personal transaction statistics
              this.getPersonalTransactionsStats();

            } else {
              this.sensNumerikInfos.actualMonthEncours = "Erreur de chargement";
              this.sensNumerikInfos.previousMonthEncours = "Erreur de chargement";
              this.showAlert('warning', "Erreur", response.data.message);
            }
          })
          .catch(() => {
            this.sensNumerikInfos.actualMonthEncours = "Erreur de chargement";
            this.sensNumerikInfos.previousMonthEncours = "Erreur de chargement";
          });
    },

    /**
     * Enhanced methods for displaying personal transaction evolution
     */
    formatPercentage() {
      const percentage = this.personalTransactionsStats.percentageChange;
      if (percentage === 0) return "0%";
      return percentage > 0 ? `+${percentage.toFixed(1)}%` : `${percentage.toFixed(1)}%`;
    },

    getDifferenceColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "success lighten-4";
      if (diff < 0) return "error lighten-4";
      return "grey lighten-3";
    },

    getDifferenceIcon() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "mdi-trending-up";
      if (diff < 0) return "mdi-trending-down";
      return "mdi-minus";
    },

    getDifferenceIconColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "success";
      if (diff < 0) return "error";
      return "grey";
    },

    getDifferenceTextColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "success--text";
      if (diff < 0) return "error--text";
      return "grey--text";
    },

    getPercentageCardColor() {
      const percentage = this.personalTransactionsStats.percentageChange;
      if (percentage > 0) return "success lighten-4";
      if (percentage < 0) return "error lighten-4";
      return "grey lighten-3";
    },

    getPercentageIcon() {
      const percentage = this.personalTransactionsStats.percentageChange;
      if (percentage > 0) return "mdi-trending-up";
      if (percentage < 0) return "mdi-trending-down";
      return "mdi-equal";
    },

    getPercentageIconColor() {
      const percentage = this.personalTransactionsStats.percentageChange;
      if (percentage > 0) return "success";
      if (percentage < 0) return "error";
      return "grey";
    },

    getPercentageTextColor() {
      const percentage = this.personalTransactionsStats.percentageChange;
      if (percentage > 0) return "success--text";
      if (percentage < 0) return "error--text";
      return "grey--text";
    },

    getTrendCardColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "primary lighten-4";
      if (diff < 0) return "warning lighten-4";
      return "grey lighten-3";
    },

    getTrendIcon() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "mdi-arrow-up-bold";
      if (diff < 0) return "mdi-arrow-down-bold";
      return "mdi-arrow-right-bold";
    },

    getTrendIconColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "primary";
      if (diff < 0) return "warning";
      return "grey";
    },

    getTrendTextColor() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "primary--text";
      if (diff < 0) return "warning--text";
      return "grey--text";
    },

    getTrendText() {
      const diff = this.personalTransactionsStats.difference;
      if (diff > 0) return "HAUSSE";
      if (diff < 0) return "BAISSE";
      return "STABLE";
    },

    // Enhanced method: Format numbers with thousands separator
    formatNumberWithCommas(number) {
      return number.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },

    depthDate() {
      if (this.depthVal) {
        sessionStorage.setItem("depthValChoosedd", this.depthVal.value);
        sessionStorage.setItem("depthValChoosedt", this.depthVal.name);
      }
      this.getData();
    },

    heightDate() {
      if (this.heightVal) {
        sessionStorage.setItem("heightValChoosedd", this.heightVal.value);
        sessionStorage.setItem("heightValChoosedt", this.heightVal.name);
      }
      this.getData();
    },

    cleared() {
      this.searchName = "";
    },

    search() {
      // Search is now automatic via computed filteredTransactions
    },

    async exportToExcel() {
      this.exportLoading = true;

      try {
        const dataToExport = [];

        // Headers
        dataToExport.push([
          "Entreprise",
          "Nom",
          "Prenom",
          "Matricule",
          "Date Commande",
          "Montant (FCFA)",
          "Type Operation",
          "Moyen de Paiement",
          "Statut",
          "Niveau",
          "Origine",
          "Informations",
          "Date Transaction"
        ]);

        // Data
        this.filteredTransactions.forEach(item => {
          dataToExport.push([
            item.entreprise,
            item.nom,
            item.prenom,
            item.matricule,
            this.convertDate(item.dateCommande),
            item.montant,
            item.typeOperation,
            item.paiement,
            item.statut,
            this.formatNiveau(item.niveau),
            item.origine || '',
            item.infos || '',
            this.convertDate(item.dateTransaction)
          ]);
        });

        // Create Excel file
        const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();

        // Adjust column widths
        const columnWidths = [
          {wch: 15}, {wch: 15}, {wch: 15}, {wch: 12},
          {wch: 15}, {wch: 12}, {wch: 15}, {wch: 15},
          {wch: 10}, {wch: 12}, {wch: 12}, {wch: 20}, {wch: 18}
        ];
        worksheet['!cols'] = columnWidths;

        XLSX.utils.book_append_sheet(workbook, worksheet, "Rapports Restaurant");

        // File name
        const currentDate = new Date().toISOString().split('T')[0];
        const fileName = `Rapports_Restaurant_${this.infosEnterpriseReference.nom}_${currentDate}.xlsx`;

        XLSX.writeFile(workbook, fileName);

        this.showAlert("success", "Export réussi", `Le fichier ${fileName} a été téléchargé avec succès`);

      } catch (error) {
        console.error("Erreur lors de l'export Excel:", error);
        this.showAlert("error", "Erreur d'export", "Impossible d'exporter les données vers Excel");
      } finally {
        this.exportLoading = false;
      }
    },

    viewTransactionDetails(transaction) {
      this.selectedTransaction = transaction;
      this.detailsDialog = true;
    },

    canDelete(transaction) {
      return sessionStorage.getItem("userId")?.toLowerCase() === "admin" &&
          transaction.statut?.toLowerCase() === "success" &&
          transaction.origine?.toLowerCase() !== "internet";
    },

    async deleteTransaction(transaction) {
      if (!this.canDelete(transaction)) {
        this.showAlert("error", "Action non autorisée", "Vous n'avez pas les droits pour supprimer cette transaction");
        return;
      }

      if (confirm("Êtes-vous sûr de vouloir supprimer cette transaction ?")) {
        this.loading = true;
        try {
          const url = `${this.$store.getters.user.baseUrl}/wallet/delete_wallet_transaction_by_uuid/${transaction.transactionUid}/${sessionStorage.getItem("accountId")}`;
          const response = await apiClient.delete(url);

          if (response.data.status === 200) {
            this.showAlert("success", "Succès", response.data.message);
            await this.getData();
          } else {
            this.showAlert("error", "Erreur", response.data.message);
          }
        } catch (error) {
          this.showAlert("error", "Erreur", "Impossible de supprimer la transaction");
        } finally {
          this.loading = false;
        }
      }
    },

    // Enhanced utility methods
    formatNumber(value) {
      if (!value || value === 0) return "0";
      return new Intl.NumberFormat('fr-FR').format(value);
    },

    getInitials(nom, prenom) {
      const n = nom ? nom.charAt(0).toUpperCase() : "";
      const p = prenom ? prenom.charAt(0).toUpperCase() : "";
      return n + p;
    },

    getEmployeeColor(matricule) {
      const colors = ['#1976D2', '#388E3C', '#F57C00', '#7B1FA2', '#D32F2F', '#00796B', '#5D4037', '#455A64'];
      const index = matricule ? matricule.length % colors.length : 0;
      return colors[index];
    },

    getDateColor(date) {
      return "primary";
    },

    getStatusColor(status) {
      switch (status?.toLowerCase()) {
        case "success":
          return "success";
        case "failed":
          return "error";
        case "cancelled":
          return "warning";
        default:
          return "grey";
      }
    },

    getStatusColorHex(status) {
      switch (status?.toLowerCase()) {
        case "success":
          return "#4CAF50";
        case "failed":
          return "#F44336";
        case "cancelled":
          return "#FF9800";
        default:
          return "#9E9E9E";
      }
    },

    getNiveauColor(niveau) {
      switch (niveau) {
        case 'PRINCIPAL':
          return 'primary';
        case 'SECONDAIRE_COMPANY':
          return 'orange';
        case 'SECONDAIRE_PERSONNEL':
          return 'purple';
        default:
          return 'grey';
      }
    },

    formatNiveau(niveau) {
      switch (niveau) {
        case 'PRINCIPAL':
          return 'PRINCIPAL';
        case 'SECONDAIRE_COMPANY':
          return 'ENTREPRISE';
        case 'SECONDAIRE_PERSONNEL':
          return 'PERSONNEL';
        default:
          return niveau || 'N/A';
      }
    },

    getCurrentMonthName() {
      return this.financialDetails.monthActual || "Mois Actuel";
    },

    getPreviousMonthName() {
      return this.financialDetails.monthPrevious || "Mois Precedent";
    },

    convertDate(val) {
      if (!val) return "";
      return moment(val).format("DD-MM-YYYY");
    },

    datefull(val) {
      if (!val) return "";
      return moment(val).format("DD-MM-YYYY HH:mm:ss");
    },

    showAlert(color, title, message) {
      this.alertColor = color;
      this.alertTitle = title;
      this.alertMessage = message;
      this.alertDialog = true;
    }
  }
};
</script>

<style scoped>
.v-card-title {
  font-weight: 600;
  font-size: 1.1rem;
}

.v-data-table {
  font-size: 0.9rem;
}

/* Style pour les en-tetes de colonnes */
.v-data-table >>> thead tr th {
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  color: #1976D2 !important;
  background-color: #F5F5F5 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px !important;
}

/* Ajouter une bordure sous les en-tetes */
.v-data-table >>> thead tr {
  border-bottom: 2px solid #E0E0E0;
}

/* Style au survol des en-tetes */
.v-data-table >>> thead tr th:hover {
  background-color: #EEEEEE !important;
  cursor: pointer;
}

/* Amélioration de l'apparence des cartes */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
}

/* Style pour les statistiques */
.text-caption {
  font-size: 0.75rem !important;
  color: #666;
}

/* Responsive */
@media (max-width: 600px) {
  .v-card-title {
    font-size: 1rem;
  }

  h2 {
    font-size: 1.5rem !important;
  }
}

/* Animation pour les chips */
.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
}

/* Style pour les boutons de sélection de période */
.v-btn-toggle {
  flex-wrap: wrap;
  display: inline-flex !important;
}

.v-btn-toggle .v-btn {
  min-width: 120px;
  margin: 2px;
}

/* Amélioration de la transition */
.v-expand-transition {
  transition: all 0.3s ease-in-out;
}

/* Styles responsive pour les onglets */
.tabs-responsive {
  transition: all 0.3s ease;
}

/* Mobile styles */
@media (max-width: 959px) {
  .tabs-responsive >>> .v-tab {
    min-width: auto !important;
    padding: 0 8px !important;
    font-size: 0.75rem !important;
    flex-direction: row !important;
    text-transform: none !important;
  }

  .tabs-responsive >>> .v-tab .v-icon {
    margin-bottom: 0 !important;
    margin-right: 4px !important;
  }

  .tabs-responsive >>> .v-tabs-bar {
    height: 48px !important;
  }

  .tabs-responsive >>> .v-tab--active {
    background-color: rgba(25, 118, 210, 0.1) !important;
  }
}

/* Tablet styles */
@media (min-width: 960px) and (max-width: 1263px) {
  .tabs-responsive >>> .v-tab {
    min-width: 120px !important;
    padding: 0 12px !important;
    font-size: 0.8rem !important;
  }
}

/* Desktop styles */
@media (min-width: 1264px) {
  .tabs-responsive >>> .v-tab {
    min-width: 160px !important;
    padding: 0 16px !important;
    font-size: 0.875rem !important;
  }
}

/* Amélioration de la lisibilité sur mobile */
@media (max-width: 599px) {
  .tabs-responsive >>> .v-tab {
    font-size: 0.7rem !important;
    padding: 0 6px !important;
  }

  .tabs-responsive >>> .v-tab .v-icon {
    font-size: 18px !important;
  }
}

/* Scroll horizontal sur très petits écrans */
@media (max-width: 479px) {
  .tabs-responsive {
    overflow-x: auto !important;
  }

  .tabs-responsive >>> .v-tabs-bar {
    flex-wrap: nowrap !important;
  }

  .tabs-responsive >>> .v-tab {
    flex: 0 0 auto !important;
    white-space: nowrap !important;
  }
}

   /* Assurer une largeur minimale pour éviter l'entrelacement */
 .v-select >>> .v-input__control {
   min-width: 180px;
 }

.v-text-field >>> .v-input__control {
  min-width: 200px;
}

/* Améliorer l'espacement sur petits écrans */
@media (max-width: 960px) {
  .v-col {
    padding: 6px 12px;
  }
}

/* Styles pour les onglets - version simplifiée */
@media (max-width: 960px) {
  .v-tabs >>> .v-tab {
    flex-direction: row !important;
    min-width: auto !important;
    padding: 0 12px !important;
  }

  .v-tabs >>> .v-tab .v-icon {
    margin-bottom: 0 !important;
    margin-right: 8px !important;
  }

  .v-tabs >>> .v-tabs-bar {
    justify-content: center !important;
  }
}

@media (max-width: 600px) {
  .v-tabs >>> .v-tab {
    font-size: 0.75rem !important;
    padding: 0 8px !important;
  }

  .v-tabs >>> .v-tab .v-icon {
    font-size: 18px !important;
    margin-right: 4px !important;
  }
}

</style>